import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  Download,
  ExternalLink,
  ArrowUpRight,
  FileText,
  FolderKanban,
  Link2,
  TriangleAlert,
} from 'lucide-react';
import { buildAssistantGoogleWorkspaceAction, isAssistantSpreadsheetArtifact } from '@triedge/domain-core';
import type {
  AssistantPresentationChartItem,
  AssistantPresentationChecklistItem,
  AssistantPresentationActionLink,
  AssistantActiveRouteContextEnvelope,
  AssistantPresentationArtifactItem,
  AssistantOpenArtifactInGoogleSheetsHandler,
  AssistantPolicyEventHandler,
  IntellioAssistantArtifactActionDescriptor,
  JsonObject,
  AssistantPresentationConfirmationTone,
  AssistantPresentationMetricItem,
  AssistantPresentationModel,
  AssistantPresentationPageSize,
  AssistantPresentationPaginationConfig,
  AssistantPresentationResultCard,
  AssistantPresentationSection,
  AssistantPresentationSectionType,
  AssistantPresentationSourceItem,
  AssistantPresentationTableCell,
  AssistantPresentationTableColumn,
  AssistantPresentationTableRow,
  AssistantPresentationTaskProposal,
  AssistantPresentationTaskThread,
  AssistantPresentationResultCardsDisplayStrategy,
} from '../types';
import { formatMessage, type MessageFormatStyles } from '../utils/messageFormat';
import {
  resolveAssistantArtifactDownloadFileName,
  resolveAssistantArtifactFormat,
  resolveAssistantSpreadsheetFallbackFileExtension,
} from '../utils/assistantArtifactDownload';
import styles from '../styles/AIAssistant.module.css';
import { AssistantMeasuredDisclosure } from './internal/AssistantMeasuredDisclosure';
import { Tooltip } from '../ui/primitives.js';

type AssistantPresentationRendererContext = {
  formatTextContent: (text: string) => React.ReactNode;
};

type ResultCardsDisplayStrategy = NonNullable<AssistantPresentationResultCardsDisplayStrategy>;

export type AssistantPresentationSectionRenderer = (
  section: AssistantPresentationSection,
  context: AssistantPresentationRendererContext,
) => React.ReactNode;

export type AssistantPresentationTextRevealState = {
  active: boolean;
  content: React.ReactNode;
  phase?: 'revealing' | 'handoff' | 'settled';
  showCursor?: boolean;
};

export type AssistantPresentationRendererProps = {
  presentation: AssistantPresentationModel;
  formatTextContent?: (text: string) => React.ReactNode;
  textRevealState?: AssistantPresentationTextRevealState | null;
  sectionRenderers?: Partial<Record<AssistantPresentationSectionType, AssistantPresentationSectionRenderer>>;
  onSourceClick?: (source: AssistantPresentationSourceItem, index: number) => void;
  getSourceTooltip?: (source: AssistantPresentationSourceItem, index: number) => string | undefined;
  onTaskProposalStart?: (proposalId: string) => void;
  onTaskProposalAskInChat?: (proposalId: string) => void;
  onTaskProposalDismiss?: (proposalId: string) => void;
  activeRouteContext?: AssistantActiveRouteContextEnvelope | null;
  onAssistantPolicyEvent?: AssistantPolicyEventHandler;
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler;
};

const DEFAULT_FORMAT_STYLES: MessageFormatStyles = styles as MessageFormatStyles;
const COMPACT_EXPORT_FORMAT_MENU_DEVIATION_ID = 'compact-export-format-menu-v1';
const RESULT_CARD_RESPONSE_KIND_STRATEGY: Partial<Record<NonNullable<AssistantPresentationModel['responseKind']>, ResultCardsDisplayStrategy>> = {
  target_gallery: 'target_gallery',
};
const PRESENTATION_PAGINATION_ITEM_THRESHOLD = 12;
const PRESENTATION_DEFAULT_PAGE_SIZE: AssistantPresentationPageSize = 8;
const PRESENTATION_PAGE_SIZE_OPTIONS = [5, 8, 16, 32] as const satisfies readonly AssistantPresentationPageSize[];
const MULTI_ARTIFACT_BROWSER_SECTION_TYPES = new Set<AssistantPresentationSectionType>([
  'chart',
  'table',
  'artifacts',
  'sources',
]);
const MULTI_ARTIFACT_BROWSER_LABELS: Partial<Record<AssistantPresentationSectionType, string>> = {
  chart: 'Chart',
  table: 'Table',
  artifacts: 'Export',
  sources: 'Sources',
};
const COMPACT_REPEATED_TABLE_TAB_WORD_LIMIT = 3;
const COMPACT_REPEATED_TABLE_TAB_MAX_LENGTH = 24;

type MultiArtifactBrowserTab = {
  id: string;
  label: string;
  section: AssistantPresentationSection;
  originalIndex: number;
};

const deriveRepeatedTableTabLabel = (
  section: AssistantPresentationSection,
  count: number,
): string => {
  const title = typeof section.title === 'string'
    ? section.title.replace(/\s+/g, ' ').trim()
    : '';
  const semanticLabel = title
    .split(/\s+/)
    .filter((word) => !/^table$/i.test(word))
    .slice(0, COMPACT_REPEATED_TABLE_TAB_WORD_LIMIT)
    .join(' ')
    .trim();
  if (semanticLabel) {
    return semanticLabel.length > COMPACT_REPEATED_TABLE_TAB_MAX_LENGTH
      ? `${semanticLabel.slice(0, COMPACT_REPEATED_TABLE_TAB_MAX_LENGTH - 1).trim()}...`
      : semanticLabel;
  }
  return count === 2 ? 'More' : `More ${count - 1}`;
};

const defaultFormatTextContent = (text: string): React.ReactNode => (
  <>{formatMessage(text, [], undefined, DEFAULT_FORMAT_STYLES)}</>
);

const resolveMetricToneClass = (tone: AssistantPresentationMetricItem['tone']): string => {
  switch (tone) {
    case 'info':
      return styles.presentationMetricCardInfo;
    case 'success':
      return styles.presentationMetricCardSuccess;
    case 'warning':
      return styles.presentationMetricCardWarning;
    default:
      return styles.presentationMetricCardNeutral;
  }
};

const resolveChecklistIcon = (status: AssistantPresentationChecklistItem['status']) => {
  if (status === 'ready') {
    return <CircleCheckBig size={14} className={styles.presentationChecklistStatusReady} />;
  }
  if (status === 'blocked' || status === 'watch') {
    return <CircleAlert size={14} className={styles.presentationChecklistStatusWatch} />;
  }
  return <span className={styles.presentationChecklistStatusTodo} aria-hidden="true" />;
};

const buildMultiArtifactBrowserTabs = (
  presentation: AssistantPresentationModel,
): MultiArtifactBrowserTab[] => {
  if (presentation.responseKind !== 'multi_artifact_insight') {
    return [];
  }
  const seenByType = new Map<AssistantPresentationSectionType, number>();
  return presentation.sections.flatMap((section, index) => {
    if (!MULTI_ARTIFACT_BROWSER_SECTION_TYPES.has(section.type)) {
      return [];
    }
    const nextCount = (seenByType.get(section.type) ?? 0) + 1;
    seenByType.set(section.type, nextCount);
    const fallbackLabel = MULTI_ARTIFACT_BROWSER_LABELS[section.type] ?? section.type;
    const explicitLabel = readOptionalNonEmptyString(section.tabLabel);
    const repeatedFallbackLabel = section.type === 'table'
      ? deriveRepeatedTableTabLabel(section, nextCount)
      : `${fallbackLabel} ${nextCount}`;
    return [{
      id: `${section.id ?? `${section.type}-${index + 1}`}-artifact-tab`,
      label: explicitLabel ?? (nextCount > 1 ? repeatedFallbackLabel : fallbackLabel),
      section,
      originalIndex: index,
    }];
  });
};

type DownloadablePresentationArtifact = AssistantPresentationArtifactItem & {
  href: string;
};

type ExportMenuPresentationArtifact = AssistantPresentationArtifactItem;

export type AssistantGoogleSheetsExportMenuAction = {
  item: AssistantPresentationArtifactItem;
  state: NonNullable<AssistantPresentationArtifactItem['googleWorkspace']>['state'];
  fileUrl?: string;
  connectHref?: string;
  type: 'open' | 'connect';
};

type GenericArtifactMenuAction = {
  item: AssistantPresentationArtifactItem;
  action: IntellioAssistantArtifactActionDescriptor;
};

type StructuredPresentationExportRow = {
  sectionType: string;
  sectionTitle: string;
  itemId: string;
  field: string;
  value: string;
  detail: string;
};

type TableDataExportPayload = {
  artifactType: 'AssistantPresentationTableDataExport';
  schemaVersion: '1.0.0';
  table: {
    id: string;
    title: string | null;
    rowCount: number;
    columnCount: number;
  };
  columns: Array<{
    id: string;
    label: string;
    align: AssistantPresentationTableColumn['align'];
  }>;
  rows: Array<{
    rowId: string;
    rowIndex: number;
    values: Record<string, string>;
    cells: Array<{
      columnId: string;
      columnLabel: string;
      text: string;
      detail: string | null;
      href: string | null;
      title: string | null;
      external: boolean;
    }>;
  }>;
};

const isDownloadablePresentationArtifact = (
  item: AssistantPresentationArtifactItem,
): item is DownloadablePresentationArtifact => (
  typeof item.href === 'string' && item.href.trim().length > 0
);

const isGoogleSheetsWorkspaceAction = (
  item: AssistantPresentationArtifactItem,
): item is AssistantPresentationArtifactItem & {
  googleWorkspace: NonNullable<AssistantPresentationArtifactItem['googleWorkspace']>;
} => item.googleWorkspace?.kind === 'google_sheets';

const readOptionalNonEmptyString = (value: unknown): string | undefined => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text.length > 0 ? text : undefined;
};

const normalizeExportText = (value: unknown): string =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

const tableCellExportText = (cell: string | AssistantPresentationTableCell | undefined): {
  value: string;
  detail: string;
} => {
  if (cell && typeof cell === 'object') {
    return {
      value: normalizeExportText(cell.text),
      detail: normalizeExportText(cell.detail),
    };
  }
  return {
    value: normalizeExportText(cell),
    detail: '',
  };
};

const pushExportRow = (
  rows: StructuredPresentationExportRow[],
  input: Omit<Partial<StructuredPresentationExportRow>, 'value' | 'detail'> & {
    sectionType: string;
    field: string;
    value: unknown;
    detail?: unknown;
  },
) => {
  const value = normalizeExportText(input.value);
  const detail = normalizeExportText(input.detail);
  if (!value && !detail) {
    return;
  }
  rows.push({
    sectionType: input.sectionType,
    sectionTitle: normalizeExportText(input.sectionTitle),
    itemId: normalizeExportText(input.itemId),
    field: normalizeExportText(input.field),
    value,
    detail,
  });
};

const collectStructuredExportRows = (
  sections: AssistantPresentationSection[],
): StructuredPresentationExportRow[] => {
  const rows: StructuredPresentationExportRow[] = [];
  sections.forEach((section, sectionIndex) => {
    const sectionTitle = section.title ?? '';
    if (section.type === 'table') {
      section.rows.forEach((row, rowIndex) => {
        section.columns.forEach((column, columnIndex) => {
          const cell = tableCellExportText(row.cells[columnIndex]);
          pushExportRow(rows, {
            sectionType: section.type,
            sectionTitle,
            itemId: normalizeExportText(row.id) || `row-${rowIndex + 1}`,
            field: column.label || column.id,
            value: cell.value,
            detail: cell.detail,
          });
        });
      });
      return;
    }
    if (section.type === 'chart') {
      section.items.forEach((item) => {
        pushExportRow(rows, {
          sectionType: section.type,
          sectionTitle: section.title ?? section.chartTitle ?? '',
          itemId: item.id,
          field: item.label,
          value: item.value,
          detail: item.tone ?? '',
        });
      });
      return;
    }
    if (section.type === 'metric_grid') {
      section.items.forEach((item) => {
        pushExportRow(rows, {
          sectionType: section.type,
          sectionTitle,
          itemId: item.id,
          field: item.label,
          value: item.value,
          detail: item.detail ?? item.tone ?? '',
        });
      });
      return;
    }
    if (section.type === 'checklist') {
      section.items.forEach((item) => {
        pushExportRow(rows, {
          sectionType: section.type,
          sectionTitle,
          itemId: item.id,
          field: item.label,
          value: item.status ?? 'todo',
          detail: item.detail ?? '',
        });
      });
      return;
    }
    if (section.type === 'result_cards') {
      section.cards.forEach((card) => {
        pushExportRow(rows, {
          sectionType: section.type,
          sectionTitle,
          itemId: card.id,
          field: card.label,
          value: [card.badge, card.meta, card.preview].map(normalizeExportText).filter(Boolean).join(' | '),
          detail: normalizeExportText(card.eyebrow),
        });
        (card.detailRows ?? []).forEach((detailRow) => {
          pushExportRow(rows, {
            sectionType: section.type,
            sectionTitle,
            itemId: card.id,
            field: detailRow.label,
            value: detailRow.value,
            detail: card.label,
          });
        });
      });
      return;
    }
    if (section.type === 'confirmation') {
      pushExportRow(rows, {
        sectionType: section.type,
        sectionTitle,
        itemId: section.id ?? `confirmation-${sectionIndex + 1}`,
        field: section.titleText,
        value: section.summary,
        detail: section.meta ?? section.tone ?? '',
      });
      return;
    }
    if (section.type === 'task_proposal') {
      pushExportRow(rows, {
        sectionType: section.type,
        sectionTitle,
        itemId: section.proposal.proposalId,
        field: section.proposal.title,
        value: section.proposal.summary,
        detail: section.proposal.question ?? '',
      });
      return;
    }
    if (section.type === 'task_thread') {
      pushExportRow(rows, {
        sectionType: section.type,
        sectionTitle,
        itemId: section.thread.threadId,
        field: section.thread.title,
        value: section.thread.stateLabel || section.thread.state,
        detail: section.thread.statusMessage ?? section.thread.detailsSummary ?? '',
      });
      (section.thread.events ?? []).forEach((event, eventIndex) => {
        pushExportRow(rows, {
          sectionType: section.type,
          sectionTitle,
          itemId: event.id ?? `${section.thread.threadId}-event-${eventIndex + 1}`,
          field: event.label,
          value: event.state ?? event.summary ?? event.content ?? '',
          detail: event.summary ?? event.content ?? '',
        });
      });
    }
  });
  return rows;
};

const csvEscape = (value: unknown): string => {
  const text = normalizeExportText(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const encodeStructuredRowsCsvDataUrl = (rows: StructuredPresentationExportRow[]): string => {
  const headers: Array<keyof StructuredPresentationExportRow> = [
    'sectionType',
    'sectionTitle',
    'itemId',
    'field',
    'value',
    'detail',
  ];
  const lines = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => headers.map((header) => csvEscape(row[header])).join(',')),
  ];
  return `data:text/csv;charset=utf-8,${encodeURIComponent(lines.join('\n'))}`;
};

const encodeStructuredRowsJsonDataUrl = (rows: StructuredPresentationExportRow[]): string =>
  `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(rows, null, 2))}`;

const collectStructuredDataExportArtifacts = (
  sections: AssistantPresentationSection[],
): ExportMenuPresentationArtifact[] => {
  const rows = collectStructuredExportRows(sections);
  if (rows.length === 0) {
    return [];
  }
  return [
    {
      id: 'assistant-response-structured-data-csv',
      label: 'Response structured data CSV',
      kind: 'csv',
      badge: 'Spreadsheet',
      meta: 'assistant-response-structured-data.csv',
      href: encodeStructuredRowsCsvDataUrl(rows),
      sourceRef: 'assistant-response:structured-data',
      actionLabel: 'Download CSV',
    },
    {
      id: 'assistant-response-structured-data-json',
      label: 'Response structured data JSON',
      kind: 'json',
      badge: 'JSON',
      meta: 'assistant-response-structured-data.json',
      href: encodeStructuredRowsJsonDataUrl(rows),
      sourceRef: 'assistant-response:structured-data',
      actionLabel: 'Download JSON',
    },
  ];
};

const isBlankGoogleSheetsUrl = (value: unknown): boolean => {
  try {
    const parsed = new URL(readOptionalNonEmptyString(value) ?? '');
    return parsed.protocol === 'https:'
      && parsed.hostname.toLowerCase() === 'sheets.new'
      && parsed.pathname.replace(/\/+$/, '') === '';
  } catch {
    return false;
  }
};

export const ASSISTANT_GOOGLE_SHEETS_RETENTION_NOTICE =
  'This Intellio-owned temporary Sheet is deleted after 24 hours. Make your own copy in Google Sheets to keep it.';

const safeFileStem = (value: unknown): string => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]{1,8}$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'assistant-artifact';
};

const resolveArtifactFormat = (item: AssistantPresentationArtifactItem): string => {
  return resolveAssistantArtifactFormat(item);
};

const downloadPresentationArtifact = (item: DownloadablePresentationArtifact): void => {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }
  const anchor = document.createElement('a');
  anchor.href = item.href;
  anchor.download = resolveAssistantArtifactDownloadFileName(item);
  anchor.style.display = 'none';
  anchor.setAttribute('aria-hidden', 'true');
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

const uniqueDownloadableMenuItems = (
  items: DownloadablePresentationArtifact[],
): DownloadablePresentationArtifact[] => {
  const seenFormats = new Set<string>();
  return items.filter((item) => {
    const format = resolveArtifactFormat(item).toLowerCase();
    if (seenFormats.has(format)) {
      return false;
    }
    seenFormats.add(format);
    return true;
  });
};

export const resolveAssistantGoogleSheetsExportAction = (
  items: readonly AssistantPresentationArtifactItem[],
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler,
): AssistantGoogleSheetsExportMenuAction | null => {
  for (const item of items) {
    if (!isGoogleSheetsWorkspaceAction(item)) {
      continue;
    }
    const workspace = item.googleWorkspace;
    const fileUrl = readOptionalNonEmptyString(workspace.fileUrl);
    if (workspace.state === 'ready' && fileUrl) {
      return { item, state: workspace.state, fileUrl, type: 'open' };
    }
  }

  for (const item of items) {
    if (!isGoogleSheetsWorkspaceAction(item)) {
      continue;
    }
    const workspace = item.googleWorkspace;
    const workbookArtifactPath = readOptionalNonEmptyString(workspace.workbookArtifactPath);
    const artifactDownloadUrl = readOptionalNonEmptyString(workspace.artifactDownloadUrl)
      ?? readOptionalNonEmptyString(item.href);
    const fileName = readOptionalNonEmptyString(workspace.fileName);
    if (
      workspace.state === 'exportable'
      && Boolean(workbookArtifactPath || artifactDownloadUrl)
      && fileName
      && onOpenArtifactInGoogleSheets
    ) {
      return { item, state: workspace.state, type: 'open' };
    }
  }

  for (const item of items) {
    if (!isGoogleSheetsWorkspaceAction(item)) {
      continue;
    }
    const workspace = item.googleWorkspace;
    const connectHref = readOptionalNonEmptyString(workspace.connectHref);
    if (workspace.state === 'connect_required' && connectHref) {
      return { item, state: workspace.state, connectHref, type: 'connect' };
    }
  }

  return null;
};

const openExternalArtifactUrl = (url: string): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const openAssistantGoogleSheetsExportAction = async (
  action: AssistantGoogleSheetsExportMenuAction,
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler,
): Promise<string> => {
  if (action.type !== 'open') {
    throw new Error('Google Sheets export is not openable from this action.');
  }
  const url = action.fileUrl ?? await onOpenArtifactInGoogleSheets?.(action.item);
  const normalizedUrl = readOptionalNonEmptyString(url);
  if (!normalizedUrl) {
    throw new Error('Google Sheets export did not return a file URL.');
  }
  if (isBlankGoogleSheetsUrl(normalizedUrl)) {
    throw new Error('Google Sheets export did not return an artifact-backed file URL.');
  }
  openExternalArtifactUrl(normalizedUrl);
  return normalizedUrl;
};

export type AssistantGoogleSheetsExportActionItemProps = {
  action: AssistantGoogleSheetsExportMenuAction;
  opening?: boolean;
  disabled?: boolean;
  onOpen: () => void;
  buttonClassName?: string;
  testId?: string;
  openLabel?: string;
  openingLabel?: string;
};

export const AssistantGoogleSheetsExportActionItem = ({
  action,
  opening = false,
  disabled = false,
  onOpen,
  buttonClassName,
  testId = 'ai-presentation-open-google-sheets',
  openLabel = 'Open in Google Sheets',
  openingLabel = 'Opening Google Sheets',
}: AssistantGoogleSheetsExportActionItemProps) => {
  if (action.type === 'connect') {
    return (
      <a
        className={buttonClassName ?? styles.presentationExportMenuItem}
        data-testid="ai-presentation-connect-google-workspace"
        data-google-workspace-state={action.state}
        href={action.connectHref}
        role="menuitem"
        onClick={onOpen}
      >
        <span>Connect Google Workspace</span>
        <ExternalLink size={13} aria-hidden="true" />
      </a>
    );
  }

  const actionLabel = opening ? openingLabel : openLabel;
  return (
    <Tooltip
      label={ASSISTANT_GOOGLE_SHEETS_RETENTION_NOTICE}
      withArrow
      position="bottom"
      color="dark"
      multiline
      w={260}
      transitionProps={{ transition: 'fade', duration: 120 }}
    >
      <button
        type="button"
        className={buttonClassName ?? styles.presentationExportMenuItem}
        data-testid={testId}
        data-google-workspace-state={action.state}
        data-google-workspace-status={
          action.item.googleWorkspace?.statusLabel ?? undefined
        }
        role="menuitem"
        disabled={disabled || opening}
        title={ASSISTANT_GOOGLE_SHEETS_RETENTION_NOTICE}
        onClick={onOpen}
      >
        <span>{actionLabel}</span>
        <ExternalLink size={13} aria-hidden="true" />
      </button>
    </Tooltip>
  );
};

const artifactHasGenericAction = (item: AssistantPresentationArtifactItem): boolean => (
  (item.actions ?? []).some((action) => !action.disabledReason)
);

const resolveGoogleSheetsCapableArtifact = (
  item: AssistantPresentationArtifactItem,
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler,
): AssistantPresentationArtifactItem => {
  if (!onOpenArtifactInGoogleSheets || !isAssistantSpreadsheetArtifact(item)) {
    return item;
  }

  const googleWorkspace = buildAssistantGoogleWorkspaceAction(item, {
    connected: true,
    supportedEnvironment: true,
    statusLabel: item.googleWorkspace?.statusLabel ?? 'Open this spreadsheet artifact in Google Sheets.',
    connectHref: item.googleWorkspace?.connectHref ?? null,
  }, {
    fallbackFileExtension: resolveAssistantSpreadsheetFallbackFileExtension(item),
  });
  if (!googleWorkspace || googleWorkspace.state === 'download_only') {
    return item;
  }

  return {
    ...item,
    googleWorkspace,
  };
};

const hasExportMenuAction = (
  item: AssistantPresentationArtifactItem,
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler,
): boolean => {
  const resolvedItem = resolveGoogleSheetsCapableArtifact(item, onOpenArtifactInGoogleSheets);
  return (
    isDownloadablePresentationArtifact(resolvedItem)
    || artifactHasGenericAction(resolvedItem)
    || Boolean(resolveAssistantGoogleSheetsExportAction([resolvedItem], onOpenArtifactInGoogleSheets))
  );
};

const hasDownloadableExportFormat = (
  items: readonly AssistantPresentationArtifactItem[],
  format: 'csv' | 'json',
): boolean => (
  items.some((item) => (
    isDownloadablePresentationArtifact(item)
    && resolveArtifactFormat(item).toLowerCase() === format
  ))
);

const collectGenericArtifactActions = (
  items: readonly AssistantPresentationArtifactItem[],
): GenericArtifactMenuAction[] => (
  items.flatMap((item) => (item.actions ?? [])
    .filter((action) => !action.disabledReason)
    .map((action) => ({ item, action })))
);

const buildArtifactPolicyEventPayload = (
  item: AssistantPresentationArtifactItem,
  action: IntellioAssistantArtifactActionDescriptor,
): JsonObject => ({
  artifactId: item.id,
  artifactLabel: item.label,
  artifactKind: item.kind ?? null,
  actionKind: action.kind,
  href: action.href ?? null,
  metadata: action.metadata ?? {},
});

export const collectAssistantPresentationExportArtifacts = (
  sections: AssistantPresentationSection[],
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler,
): AssistantPresentationArtifactItem[] => {
  const seen = new Set<string>();
  const artifacts: ExportMenuPresentationArtifact[] = [];
  sections.forEach((section) => {
    if (section.type !== 'artifacts') {
      return;
    }
    section.items.forEach((item) => {
      const resolvedItem = resolveGoogleSheetsCapableArtifact(item, onOpenArtifactInGoogleSheets);
      if (!hasExportMenuAction(resolvedItem, onOpenArtifactInGoogleSheets)) {
        return;
      }
      const googleWorkspace = resolvedItem.googleWorkspace;
      const key = [
        resolvedItem.id,
        resolvedItem.href ?? '',
        resolvedItem.meta ?? '',
        googleWorkspace?.state ?? '',
        googleWorkspace?.fileUrl ?? '',
        googleWorkspace?.workbookArtifactPath ?? '',
        googleWorkspace?.artifactDownloadUrl ?? '',
        googleWorkspace?.mimeType ?? '',
      ].join('|');
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      artifacts.push(resolvedItem);
    });
  });
  if (artifacts.length > 0) {
    const structuredFallbackArtifacts = collectStructuredDataExportArtifacts(sections)
      .map((item) => resolveGoogleSheetsCapableArtifact(item, onOpenArtifactInGoogleSheets))
      .filter((item) => hasExportMenuAction(item, onOpenArtifactInGoogleSheets))
      .filter((item) => (
        resolveArtifactFormat(item).toLowerCase() === 'csv'
        && !hasDownloadableExportFormat(artifacts, 'csv')
      ));
    return [
      ...artifacts,
      ...structuredFallbackArtifacts,
    ];
  }
  return collectStructuredDataExportArtifacts(sections)
    .map((item) => resolveGoogleSheetsCapableArtifact(item, onOpenArtifactInGoogleSheets))
    .filter((item) => hasExportMenuAction(item, onOpenArtifactInGoogleSheets));
};

const shouldRenderArtifactsSectionInline = (
  section: AssistantPresentationSection,
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler,
): boolean => (
  section.type !== 'artifacts'
  || section.items.length === 0
  || section.items.some((item) => !hasExportMenuAction(item, onOpenArtifactInGoogleSheets))
);

const resolveExportMenuTargetIndex = (
  sections: AssistantPresentationSection[],
  artifacts: ExportMenuPresentationArtifact[],
): number => {
  if (artifacts.length === 0) {
    return -1;
  }
  const accountPreviewTableIndex = sections.findIndex((section) => (
    section.type === 'table'
    && /account preview/i.test(String(section.title ?? ''))
  ));
  if (accountPreviewTableIndex >= 0) {
    return accountPreviewTableIndex;
  }
  const chartIndex = sections.findIndex((section) => section.type === 'chart');
  if (chartIndex >= 0) {
    return chartIndex;
  }
  const firstVisibleIndex = sections.findIndex((section) => section.type !== 'artifacts');
  return firstVisibleIndex >= 0 ? firstVisibleIndex : sections.findIndex((section) => section.type === 'artifacts');
};

export type AssistantPresentationExportMenuProps = {
  items: AssistantPresentationArtifactItem[];
  activeRouteContext?: AssistantActiveRouteContextEnvelope | null;
  onAssistantPolicyEvent?: AssistantPolicyEventHandler;
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler;
  testId?: string;
  buttonTestId?: string;
  listTestId?: string;
  menuItemTestIdPrefix?: string;
  genericActionTestIdPrefix?: string;
  label?: string;
  dataExportSurface?: string;
};

export const AssistantPresentationExportMenu = ({
  items,
  activeRouteContext,
  onAssistantPolicyEvent,
  onOpenArtifactInGoogleSheets,
  testId = 'ai-presentation-export-menu',
  buttonTestId = 'ai-presentation-export-menu-button',
  listTestId = 'ai-presentation-export-menu-list',
  menuItemTestIdPrefix = 'ai-presentation-export-menu-item',
  genericActionTestIdPrefix = 'ai-presentation-artifact-action',
  label = 'Export or open response data',
  dataExportSurface = 'assistant-response-data',
}: AssistantPresentationExportMenuProps) => {
  const [open, setOpen] = useState(false);
  const [openingGoogleSheets, setOpeningGoogleSheets] = useState(false);
  const [googleSheetsError, setGoogleSheetsError] = useState<string | null>(null);
  const [resolvedGoogleSheetsUrls, setResolvedGoogleSheetsUrls] = useState<Record<string, string>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = React.useId();
  const resolvedItems = useMemo(() => items.map((item) => {
    const googleSheetsCapableItem = resolveGoogleSheetsCapableArtifact(item, onOpenArtifactInGoogleSheets);
    const fileUrl = resolvedGoogleSheetsUrls[item.id];
    if (!fileUrl || !isGoogleSheetsWorkspaceAction(googleSheetsCapableItem)) {
      return googleSheetsCapableItem;
    }
    return {
      ...googleSheetsCapableItem,
      googleWorkspace: {
        ...googleSheetsCapableItem.googleWorkspace,
        state: 'ready' as const,
        fileUrl,
      },
    };
  }), [items, onOpenArtifactInGoogleSheets, resolvedGoogleSheetsUrls]);
  const googleSheetsAction = useMemo(
    () => resolveAssistantGoogleSheetsExportAction(resolvedItems, onOpenArtifactInGoogleSheets),
    [onOpenArtifactInGoogleSheets, resolvedItems],
  );
  const downloadableItems = useMemo(
    () => uniqueDownloadableMenuItems(resolvedItems.filter(isDownloadablePresentationArtifact)),
    [resolvedItems],
  );
  const genericArtifactActions = useMemo(
    () => collectGenericArtifactActions(resolvedItems),
    [resolvedItems],
  );

  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return undefined;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (items.length === 0) {
    return null;
  }

  const handleOpenGoogleSheets = async () => {
    if (!googleSheetsAction || googleSheetsAction.type !== 'open') {
      return;
    }
    setGoogleSheetsError(null);
    setOpeningGoogleSheets(true);
    try {
      const url = await openAssistantGoogleSheetsExportAction(googleSheetsAction, onOpenArtifactInGoogleSheets);
      setResolvedGoogleSheetsUrls((current) => ({
        ...current,
        [googleSheetsAction.item.id]: url,
      }));
      setOpen(false);
    } catch (error) {
      setGoogleSheetsError(error instanceof Error ? error.message : 'Google Sheets export failed.');
    } finally {
      setOpeningGoogleSheets(false);
    }
  };

  const handleGenericArtifactAction = async (
    item: AssistantPresentationArtifactItem,
    action: IntellioAssistantArtifactActionDescriptor,
  ) => {
    if (action.kind === 'download' && isDownloadablePresentationArtifact(item)) {
      downloadPresentationArtifact(item);
      setOpen(false);
      return;
    }
    const href = readOptionalNonEmptyString(action.href);
    if ((action.kind === 'open_external' || action.kind === 'open_workspace') && href) {
      openExternalArtifactUrl(href);
      setOpen(false);
      return;
    }
    if (!onAssistantPolicyEvent) {
      setGoogleSheetsError(`${action.label} is not available in this route context.`);
      return;
    }
    const result = await onAssistantPolicyEvent({
      eventId: `artifact-action-${action.id}-${Date.now()}`,
      type: 'artifact_action',
      policyEventType: action.policyEventType ?? action.kind,
      routeContextRef: activeRouteContext?.contextRef ?? null,
      routeRevision: activeRouteContext?.revision ?? null,
      routeFamily: activeRouteContext?.routeFamily ?? null,
      pathname: activeRouteContext?.pathname ?? null,
      actionId: action.id,
      artifactActionId: action.id,
      sourceRef: action.sourceRef ?? item.sourceRef ?? null,
      payload: buildArtifactPolicyEventPayload(item, action),
    });
    if (!result.accepted) {
      setGoogleSheetsError(result.message);
      return;
    }
    setOpen(false);
  };

  return (
    // Contract marker for validators: data-export-surface="assistant-response-data"
    <div
      ref={menuRef}
      className={styles.presentationExportMenu}
      data-testid={testId}
      data-ai-ui-deviation={COMPACT_EXPORT_FORMAT_MENU_DEVIATION_ID}
      data-export-surface={dataExportSurface}
    >
      <Tooltip
        label={label}
        withArrow
        position="bottom"
        color="dark"
        transitionProps={{ transition: 'fade', duration: 120 }}
      >
        <button
          type="button"
          className={styles.presentationExportMenuButton}
          data-testid={buttonTestId}
          data-ai-ui-deviation={COMPACT_EXPORT_FORMAT_MENU_DEVIATION_ID}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          title={label}
          onClick={() => setOpen((current) => !current)}
        >
          <Download size={15} aria-hidden="true" />
        </button>
      </Tooltip>
      {open ? (
        <div
          id={menuId}
          className={styles.presentationExportMenuList}
          data-testid={listTestId}
          role="menu"
          aria-label={label}
        >
          {genericArtifactActions.map(({ item, action }, index) => (
            <button
              key={`${item.id}-${action.id}`}
              type="button"
              className={styles.presentationExportMenuItem}
              data-testid={`${genericActionTestIdPrefix}-${index + 1}`}
              data-artifact-action-kind={action.kind}
              data-href={action.href ?? undefined}
              role="menuitem"
              onClick={() => {
                void handleGenericArtifactAction(item, action);
              }}
            >
              {action.label}
            </button>
          ))}
          {googleSheetsAction ? (
            <AssistantGoogleSheetsExportActionItem
              action={googleSheetsAction}
              opening={openingGoogleSheets}
              onOpen={() => {
                if (googleSheetsAction.type === 'connect') {
                  setOpen(false);
                  return;
                }
                void handleOpenGoogleSheets();
              }}
            />
          ) : null}
          {downloadableItems.map((item, index) => {
            const format = resolveArtifactFormat(item);
            const label = `Download ${format}`;
            return (
              <button
                key={item.id || `${item.href}-${index + 1}`}
                type="button"
                className={styles.presentationExportMenuItem}
                data-testid={`${menuItemTestIdPrefix}-${index + 1}`}
                data-artifact-format={format.toLowerCase()}
                role="menuitem"
                onClick={() => {
                  downloadPresentationArtifact(item);
                  setOpen(false);
                }}
              >
                {label}
              </button>
            );
          })}
          {googleSheetsError ? (
            <p className={styles.presentationExportMenuStatus} role="status">
              {googleSheetsError}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const PresentationSectionHeadingRow = ({
  title,
  fallbackTitle,
  exportMenu,
}: {
  title?: string | null;
  fallbackTitle?: string;
  exportMenu?: React.ReactNode;
}) => {
  const resolvedTitle = typeof title === 'string' && title.trim().length > 0
    ? title
    : fallbackTitle;
  if (!resolvedTitle && !exportMenu) {
    return null;
  }
  return (
    <div className={styles.presentationSectionHeadingRow}>
      {resolvedTitle ? <span className={styles.presentationSectionEyebrow}>{resolvedTitle}</span> : <span />}
      {exportMenu}
    </div>
  );
};

const composeSectionActions = (
  ...actions: Array<React.ReactNode | null | undefined | false>
): React.ReactNode => {
  const visibleActions = actions.filter((action): action is React.ReactNode => Boolean(action));
  if (visibleActions.length === 0) {
    return null;
  }
  if (visibleActions.length === 1) {
    return visibleActions[0];
  }
  return (
    <div className={styles.presentationSectionActions}>
      {visibleActions.map((action, index) => (
        <React.Fragment key={index}>{action}</React.Fragment>
      ))}
    </div>
  );
};

const renderActionLink = (
  action: AssistantPresentationActionLink | null | undefined,
  {
    testId,
    primary = false,
  }: {
    testId: string;
    primary?: boolean;
  },
) => {
  if (!action) {
    return null;
  }
  const className = primary ? styles.presentationPrimaryAction : styles.presentationSecondaryAction;
  const content = (
    <>
      <span>{action.label}</span>
      {action.external ? (
        <ExternalLink size={13} className={styles.presentationActionIcon} />
      ) : (
        <ArrowUpRight size={13} className={styles.presentationActionIcon} />
      )}
    </>
  );
  if (action.href) {
    return (
      <a
        href={action.href}
        className={className}
        data-testid={testId}
        aria-label={action.label}
        title={action.label}
        target={action.external ? '_blank' : undefined}
        rel={action.external ? 'noreferrer noopener' : undefined}
      >
        {content}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={className}
      data-testid={testId}
      data-action-id={action.actionId ?? undefined}
      disabled
    >
      {content}
    </button>
  );
};

const shouldRenderVisibleFooterAction = (action: AssistantPresentationActionLink | null | undefined): boolean =>
  Boolean(action?.href && /^visualize$/i.test(action.label.trim()));

const renderTableFooterAction = (
  action: AssistantPresentationActionLink | null | undefined,
): React.ReactNode => {
  if (!action) {
    return null;
  }
  if (shouldRenderVisibleFooterAction(action)) {
    return null;
  }
  return renderActionLink(action, { testId: 'ai-table-footer-action' });
};

const isResultCardsDisplayStrategy = (value: unknown): value is ResultCardsDisplayStrategy => (
  value === 'default' || value === 'target_gallery' || value === 'action_list'
);

const normalizeResultCardDisplayText = (value: unknown): string | null => {
  const text = readOptionalNonEmptyString(value);
  return text ?? null;
};

const resolveUniformCardText = (
  cards: AssistantPresentationResultCard[],
  readValue: (card: AssistantPresentationResultCard) => unknown,
): string | null => {
  const values = cards.map((card) => normalizeResultCardDisplayText(readValue(card))).filter(Boolean) as string[];
  if (values.length !== cards.length || values.length === 0) {
    return null;
  }
  const [first] = values;
  return values.every((value) => value.toLowerCase() === first.toLowerCase()) ? first : null;
};

const isActionListResultCards = ({
  title,
  cards,
}: {
  title?: string | null;
  cards?: AssistantPresentationResultCard[];
}): boolean => {
  const normalizedTitle = normalizeResultCardDisplayText(title)?.toLowerCase() ?? '';
  return cards != null
    && cards.length > 1
    && (
      normalizedTitle.includes('needing action')
      || normalizedTitle.includes('action needed')
      || normalizedTitle.includes('follow-up')
    );
};

const resolveResultCardsDisplayStrategy = (input: {
  sectionStrategy?: AssistantPresentationResultCardsDisplayStrategy | null;
  responseKind?: AssistantPresentationModel['responseKind'] | null;
  title?: string | null;
  cards?: AssistantPresentationResultCard[];
}): ResultCardsDisplayStrategy => {
  if (isResultCardsDisplayStrategy(input.sectionStrategy)) {
    return input.sectionStrategy;
  }
  if (input.responseKind && RESULT_CARD_RESPONSE_KIND_STRATEGY[input.responseKind]) {
    return RESULT_CARD_RESPONSE_KIND_STRATEGY[input.responseKind] ?? 'default';
  }
  if (isActionListResultCards({ title: input.title, cards: input.cards })) {
    return 'action_list';
  }
  return 'default';
};

type TargetGalleryFooterAction = {
  key: string;
  href: string;
  label: string;
  external?: boolean;
};

const collectTargetGalleryFooterActions = (
  cards: AssistantPresentationResultCard[],
): TargetGalleryFooterAction[] => {
  const seen = new Set<string>();
  const actions: TargetGalleryFooterAction[] = [];
  cards.forEach((card, index) => {
    [card.primaryAction, card.secondaryAction].forEach((action) => {
      const href = readOptionalNonEmptyString(action?.href);
      if (!href) {
        return;
      }
      const key = `${href}::${card.id}`.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      const targetLabel = readOptionalNonEmptyString(card.label) ?? `Target ${index + 1}`;
      actions.push({
        key,
        href,
        label: `Open ${targetLabel}`,
        external: action?.external,
      });
    });
  });
  return actions;
};

const normalizeTableCell = (
  cell: string | AssistantPresentationTableCell | null | undefined,
): AssistantPresentationTableCell => {
  if (typeof cell === 'string') {
    return { text: cell };
  }
  return {
    text: String(cell?.text ?? ''),
    detail: cell?.detail ?? null,
    href: cell?.href ?? null,
    title: cell?.title ?? null,
    external: cell?.external ?? false,
  };
};

const readTableCellText = (
  cell: string | AssistantPresentationTableCell | null | undefined,
): {
  text: string;
  detail: string | null;
  href: string | null;
  title: string | null;
  external: boolean;
} => {
  const normalized = normalizeTableCell(cell);
  return {
    text: normalizeExportText(normalized.text),
    detail: readOptionalNonEmptyString(normalized.detail) ?? null,
    href: readOptionalNonEmptyString(normalized.href) ?? null,
    title: readOptionalNonEmptyString(normalized.title) ?? null,
    external: normalized.external === true,
  };
};

const uniqueTableHeaderLabels = (
  columns: AssistantPresentationTableColumn[],
): string[] => {
  const seen = new Map<string, number>();
  return columns.map((column, index) => {
    const baseLabel = normalizeExportText(column.label || column.id) || `Column ${index + 1}`;
    const seenCount = seen.get(baseLabel.toLowerCase()) ?? 0;
    seen.set(baseLabel.toLowerCase(), seenCount + 1);
    return seenCount === 0 ? baseLabel : `${baseLabel} ${seenCount + 1}`;
  });
};

const encodeTableRowsCsvDataUrl = (
  columns: AssistantPresentationTableColumn[],
  rows: AssistantPresentationTableRow[],
): string => {
  const headers = uniqueTableHeaderLabels(columns);
  const hasDetailByColumn = columns.map((_, columnIndex) => rows.some((row) => readTableCellText(row.cells[columnIndex]).detail));
  const hasHrefByColumn = columns.map((_, columnIndex) => rows.some((row) => readTableCellText(row.cells[columnIndex]).href));
  const csvHeaders = columns.flatMap((_, columnIndex) => [
    headers[columnIndex] ?? `Column ${columnIndex + 1}`,
    ...(hasDetailByColumn[columnIndex] ? [`${headers[columnIndex] ?? `Column ${columnIndex + 1}`} detail`] : []),
    ...(hasHrefByColumn[columnIndex] ? [`${headers[columnIndex] ?? `Column ${columnIndex + 1}`} href`] : []),
  ]);
  const lines = [
    csvHeaders.map(csvEscape).join(','),
    ...rows.map((row) => columns.flatMap((_, columnIndex) => {
      const cell = readTableCellText(row.cells[columnIndex]);
      return [
        cell.text,
        ...(hasDetailByColumn[columnIndex] ? [cell.detail ?? ''] : []),
        ...(hasHrefByColumn[columnIndex] ? [cell.href ?? ''] : []),
      ];
    }).map(csvEscape).join(',')),
  ];
  return `data:text/csv;charset=utf-8,${encodeURIComponent(lines.join('\n'))}`;
};

const buildTableDataExportPayload = ({
  sectionId,
  title,
  columns,
  rows,
}: {
  sectionId?: string | null;
  title?: string | null;
  columns: AssistantPresentationTableColumn[];
  rows: AssistantPresentationTableRow[];
}): TableDataExportPayload => ({
  artifactType: 'AssistantPresentationTableDataExport',
  schemaVersion: '1.0.0',
  table: {
    id: readOptionalNonEmptyString(sectionId) ?? safeFileStem(title ?? 'assistant-response-table'),
    title: readOptionalNonEmptyString(title) ?? null,
    rowCount: rows.length,
    columnCount: columns.length,
  },
  columns: columns.map((column, index) => ({
    id: readOptionalNonEmptyString(column.id) ?? `column-${index + 1}`,
    label: normalizeExportText(column.label || column.id) || `Column ${index + 1}`,
    align: column.align,
  })),
  rows: rows.map((row, rowIndex) => {
    const cells = columns.map((column, columnIndex) => {
      const cell = readTableCellText(row.cells[columnIndex]);
      return {
        columnId: readOptionalNonEmptyString(column.id) ?? `column-${columnIndex + 1}`,
        columnLabel: normalizeExportText(column.label || column.id) || `Column ${columnIndex + 1}`,
        text: cell.text,
        detail: cell.detail,
        href: cell.href,
        title: cell.title,
        external: cell.external,
      };
    });
    return {
      rowId: readOptionalNonEmptyString(row.id) ?? `row-${rowIndex + 1}`,
      rowIndex: rowIndex + 1,
      values: cells.reduce<Record<string, string>>((acc, cell) => {
        acc[cell.columnId] = cell.text;
        return acc;
      }, {}),
      cells,
    };
  }),
});

const encodeTableRowsJsonDataUrl = (
  payload: TableDataExportPayload,
): string =>
  `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(payload, null, 2))}`;

const collectTableDataExportArtifacts = ({
  sectionId,
  title,
  columns,
  rows,
}: {
  sectionId?: string | null;
  title?: string | null;
  columns: AssistantPresentationTableColumn[];
  rows: AssistantPresentationTableRow[];
}): ExportMenuPresentationArtifact[] => {
  if (columns.length === 0 || rows.length === 0) {
    return [];
  }
  const tableTitle = readOptionalNonEmptyString(title) ?? 'Assistant response table';
  const stem = safeFileStem(sectionId ?? tableTitle);
  const sourceRef = `assistant-response:table-data:${stem}`;
  const payload = buildTableDataExportPayload({ sectionId, title, columns, rows });
  return [
    {
      id: `${stem}-table-data-csv`,
      label: `${tableTitle} table CSV`,
      kind: 'csv',
      badge: 'Table',
      meta: `${stem}.csv`,
      href: encodeTableRowsCsvDataUrl(columns, rows),
      sourceRef,
      actionLabel: 'Download table CSV',
    },
    {
      id: `${stem}-table-data-json`,
      label: `${tableTitle} table JSON`,
      kind: 'json',
      badge: 'Table',
      meta: `${stem}.json`,
      href: encodeTableRowsJsonDataUrl(payload),
      sourceRef,
      actionLabel: 'Download table JSON',
    },
  ];
};

const renderTableCellContent = (
  cell: string | AssistantPresentationTableCell | null | undefined,
): React.ReactNode => {
  const normalized = normalizeTableCell(cell);
  const label = normalized.text.trim();
  const actionLabel = normalized.title ?? (label ? `Open ${label}` : 'Open linked item');
  const textNode = (
    <span
      className={styles.presentationTableCellText}
      title={!normalized.href ? normalized.title ?? undefined : undefined}
    >
      {normalized.text}
    </span>
  );
  const contentNode = normalized.href ? (
    <a
      href={normalized.href}
      className={styles.presentationTableCellTextAction}
      title={actionLabel}
      aria-label={actionLabel}
      target={normalized.external ? '_blank' : undefined}
      rel={normalized.external ? 'noreferrer noopener' : undefined}
    >
      {textNode}
      {normalized.external ? <ExternalLink size={12} /> : <ArrowUpRight size={12} />}
    </a>
  ) : textNode;

  if (!normalized.detail) {
    return (
      <span className={styles.presentationTableCellInline}>
        {contentNode}
      </span>
    );
  }

  return (
    <div className={styles.presentationTableCellStack}>
      <span className={styles.presentationTableCellInline}>
        {contentNode}
      </span>
      <span className={styles.presentationTableCellDetail}>{normalized.detail}</span>
    </div>
  );
};

const toDisclosureDomId = (value: string): string => (
  value.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'assistant-presentation-disclosure'
);

const PresentationDisclosure = ({
  testId,
  disclosureId,
  summary,
  children,
  className,
  summaryClassName,
  bodyClassName,
  defaultOpen = false,
  displayMode,
  toggleTestId,
  contentTestId,
  hidden = false,
}: {
  testId: string;
  disclosureId: string;
  summary: React.ReactNode;
  children: React.ReactNode;
  className: string;
  summaryClassName: string;
  bodyClassName: string;
  defaultOpen?: boolean;
  displayMode?: 'drawer' | 'details';
  toggleTestId?: string;
  contentTestId?: string;
  hidden?: boolean;
}) => {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const contentId = `${toDisclosureDomId(disclosureId)}-content`;
  const toggleOpen = useCallback(() => {
    setOpen((current) => !current);
  }, []);

  return (
    <div
      className={className}
      hidden={hidden}
      data-testid={testId}
      data-open={open ? 'true' : 'false'}
      data-ai-diagnostics-display-mode={displayMode}
    >
      <button
        type="button"
        className={summaryClassName}
        data-testid={toggleTestId ?? `${testId}-toggle`}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={toggleOpen}
      >
        {summary}
      </button>
      {open ? (
        <AssistantMeasuredDisclosure data-testid={contentTestId ?? `${testId}-content`}>
          <div id={contentId} className={bodyClassName}>
            {children}
          </div>
        </AssistantMeasuredDisclosure>
      ) : null}
    </div>
  );
};

type ResultCardsSectionProps = {
  title?: string | null;
  cards: AssistantPresentationResultCard[];
  exportMenu?: React.ReactNode;
  pagination?: AssistantPresentationPaginationConfig | null;
};

const TABLE_PAGINATION_LABELS: PresentationPaginationLabels = {
  controlsTestId: 'ai-table-pagination-controls',
  summaryTestId: 'ai-table-pagination-summary',
  pageSizeTestIdPrefix: 'ai-table-page-size',
  previousTestId: 'ai-table-page-prev',
  nextTestId: 'ai-table-page-next',
  pageLabelTestId: 'ai-table-pagination-page',
  itemSingular: 'Row',
  itemPlural: 'Rows',
};

const RESULT_CARDS_PAGINATION_LABELS: PresentationPaginationLabels = {
  controlsTestId: 'ai-result-cards-pagination-controls',
  summaryTestId: 'ai-result-cards-pagination-summary',
  pageSizeTestIdPrefix: 'ai-result-cards-page-size',
  previousTestId: 'ai-result-cards-page-prev',
  nextTestId: 'ai-result-cards-page-next',
  pageLabelTestId: 'ai-result-cards-pagination-page',
  itemSingular: 'Result',
  itemPlural: 'Results',
};

const COMPACT_RESULT_GROUP_PAGINATION_LABELS: PresentationPaginationLabels = {
  ...RESULT_CARDS_PAGINATION_LABELS,
  itemSingular: 'Signal',
  itemPlural: 'Signals',
};

const CHECKLIST_PAGINATION_LABELS: PresentationPaginationLabels = {
  controlsTestId: 'ai-checklist-pagination-controls',
  summaryTestId: 'ai-checklist-pagination-summary',
  pageSizeTestIdPrefix: 'ai-checklist-page-size',
  previousTestId: 'ai-checklist-page-prev',
  nextTestId: 'ai-checklist-page-next',
  pageLabelTestId: 'ai-checklist-pagination-page',
  itemSingular: 'Item',
  itemPlural: 'Items',
};

const DefaultResultCardsSection = ({
  title,
  cards,
  exportMenu,
  pagination: paginationConfig,
}: ResultCardsSectionProps) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const pagination = usePresentationPagination(cards, paginationConfig, sectionRef);
  return (
    <section
      ref={sectionRef}
      className={styles.presentationSection}
      data-testid="ai-result-cards-section"
      data-result-pagination={pagination.state.enabled ? 'true' : 'false'}
    >
      <PresentationSectionHeadingRow title={title} fallbackTitle="Results" exportMenu={exportMenu} />
      <div className={styles.presentationResultGrid}>
        {pagination.state.visibleItems.map((card, index) => {
          const visibleIndex = pagination.state.enabled ? pagination.state.startIndex + index : index;
          return (
            <article
              key={card.id}
              className={styles.presentationResultCard}
              data-testid={`ai-result-card-${visibleIndex + 1}`}
            >
              <div className={styles.presentationResultHeader}>
                <div>
                  {card.eyebrow ? (
                    <p className={styles.presentationSectionEyebrow}>{card.eyebrow}</p>
                  ) : null}
                  <h4 className={styles.presentationCardTitle}>{card.label}</h4>
                  {card.meta ? <p className={styles.presentationCardMeta}>{card.meta}</p> : null}
                </div>
                {card.badge ? <span className={styles.presentationCardBadge}>{card.badge}</span> : null}
              </div>
              {card.preview ? <p className={styles.presentationCardPreview}>{card.preview}</p> : null}
              {card.detailRows?.length ? (
                <PresentationDisclosure
                  testId={`ai-result-card-details-${visibleIndex + 1}`}
                  disclosureId={`result-card-${card.id}-details`}
                  className={styles.presentationDetails}
                  summaryClassName={styles.presentationDetailsSummary}
                  bodyClassName={styles.presentationDetailsBody}
                  summary={(
                    <>
                      <span>Route details</span>
                      <ChevronDown size={14} />
                    </>
                  )}
                >
                    {card.detailRows.map((row) => (
                      <div key={`${card.id}-${row.label}`} className={styles.presentationDetailRow}>
                        <span className={styles.presentationDetailLabel}>{row.label}</span>
                        <span className={styles.presentationDetailValue}>{row.value}</span>
                      </div>
                    ))}
                </PresentationDisclosure>
              ) : null}
              <div className={styles.presentationActionRow}>
                {renderActionLink(card.primaryAction, { testId: `ai-result-card-primary-${visibleIndex + 1}`, primary: true })}
                {renderActionLink(card.secondaryAction, { testId: `ai-result-card-secondary-${visibleIndex + 1}` })}
              </div>
            </article>
          );
        })}
      </div>
      <div className={styles.presentationInlinePaginationFooter} data-has-pagination={pagination.state.enabled ? 'true' : 'false'}>
        <PresentationPaginationControls
          pagination={pagination.state}
          labels={resolvePresentationPaginationLabels(RESULT_CARDS_PAGINATION_LABELS, paginationConfig)}
          onPageIndexChange={pagination.setPageIndex}
          onPageSizeChange={pagination.setPageSize}
        />
      </div>
    </section>
  );
};

const TargetGalleryResultCardsSection = ({
  title,
  cards,
  exportMenu,
  pagination: paginationConfig,
}: ResultCardsSectionProps) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const pagination = usePresentationPagination(cards, paginationConfig, sectionRef);
  const footerActions = collectTargetGalleryFooterActions(pagination.state.visibleItems);
  return (
    <section
      ref={sectionRef}
      className={styles.presentationSection}
      data-testid="ai-result-cards-section"
      data-display-strategy="target_gallery"
      data-result-pagination={pagination.state.enabled ? 'true' : 'false'}
    >
      <PresentationSectionHeadingRow title={title} fallbackTitle="Targets" exportMenu={exportMenu} />
      <div className={styles.presentationTargetGalleryGrid} data-testid="ai-target-gallery-grid">
        {pagination.state.visibleItems.map((card, index) => {
          const visibleIndex = pagination.state.enabled ? pagination.state.startIndex + index : index;
          return (
            <article
              key={card.id}
              className={styles.presentationTargetGalleryCard}
              data-testid={`ai-target-gallery-card-${visibleIndex + 1}`}
            >
              <div className={styles.presentationTargetGalleryHeader}>
                <div className={styles.presentationTargetGalleryTitleBlock}>
                  {card.eyebrow ? (
                    <p className={styles.presentationSectionEyebrow}>{card.eyebrow}</p>
                  ) : null}
                  <h4 className={styles.presentationTargetGalleryTitle}>{card.label}</h4>
                  {card.meta ? <p className={styles.presentationCardMeta}>{card.meta}</p> : null}
                </div>
                {card.badge ? <span className={styles.presentationCardBadge}>{card.badge}</span> : null}
              </div>
              {card.preview ? (
                <p className={styles.presentationTargetGalleryReason}>{card.preview}</p>
              ) : null}
              {card.detailRows?.length ? (
                <dl
                  className={styles.presentationTargetGalleryFacts}
                  data-testid={`ai-target-gallery-facts-${visibleIndex + 1}`}
                >
                  {card.detailRows.map((row) => (
                    <div key={`${card.id}-${row.label}`} className={styles.presentationTargetGalleryFact}>
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </article>
          );
        })}
      </div>
      <div className={styles.presentationInlinePaginationFooter} data-has-pagination={pagination.state.enabled ? 'true' : 'false'}>
        <PresentationPaginationControls
          pagination={pagination.state}
          labels={resolvePresentationPaginationLabels(RESULT_CARDS_PAGINATION_LABELS, paginationConfig)}
          onPageIndexChange={pagination.setPageIndex}
          onPageSizeChange={pagination.setPageSize}
        />
      </div>
      {footerActions.length > 0 ? (
        <nav className={styles.presentationTargetGalleryRail} data-testid="ai-target-gallery-footer-rail" aria-label="Target links">
          {footerActions.map((action, index) => (
            <a
              key={action.key}
              href={action.href}
              className={styles.presentationTargetGalleryRailAction}
              data-testid={`ai-target-gallery-footer-link-${index + 1}`}
              target={action.external ? '_blank' : undefined}
              rel={action.external ? 'noreferrer noopener' : undefined}
              title={action.label}
              aria-label={action.label}
            >
              <span>{action.label}</span>
              <ArrowUpRight size={13} className={styles.presentationActionIcon} />
            </a>
          ))}
        </nav>
      ) : null}
    </section>
  );
};

const normalizeCompactResultTitle = (
  card: AssistantPresentationResultCard,
  uniformEyebrow: string | null,
): string => {
  const label = normalizeResultCardDisplayText(card.label);
  if (label && (!uniformEyebrow || label.toLowerCase() !== uniformEyebrow.toLowerCase())) {
    return label;
  }
  const preview = normalizeResultCardDisplayText(card.preview);
  if (preview) {
    const normalizedPreview = preview.replace(/^is\s+/i, '').trim();
    return normalizedPreview
      ? `${normalizedPreview.charAt(0).toUpperCase()}${normalizedPreview.slice(1)}`
      : preview;
  }
  return label ?? 'Record needing action';
};

type CompactResultListGroup = {
  id: string;
  cards: AssistantPresentationResultCard[];
  representative: AssistantPresentationResultCard;
  title: string;
  preview: string | null;
  eyebrow: string | null;
  meta: string | null;
  badge: string | null;
};

const normalizeCompactResultGroupKey = (value: string | null): string => (
  value?.trim().replace(/\s+/g, ' ').toLowerCase() ?? ''
);

const buildCompactResultListGroups = (
  cards: AssistantPresentationResultCard[],
  uniformEyebrow: string | null,
): CompactResultListGroup[] => {
  const groups = new Map<string, CompactResultListGroup>();
  for (const card of cards) {
    const title = normalizeCompactResultTitle(card, uniformEyebrow);
    const preview = normalizeResultCardDisplayText(card.preview);
    const eyebrow = normalizeResultCardDisplayText(card.eyebrow);
    const meta = normalizeResultCardDisplayText(card.meta);
    const badge = normalizeResultCardDisplayText(card.badge);
    const key = [title, preview, eyebrow, meta, badge].map(normalizeCompactResultGroupKey).join('|');
    const existing = groups.get(key);
    if (existing) {
      existing.cards.push(card);
    } else {
      groups.set(key, {
        id: card.id,
        cards: [card],
        representative: card,
        title,
        preview,
        eyebrow,
        meta,
        badge,
      });
    }
  }
  return Array.from(groups.values());
};

const resolveCompactResultPrimaryActions = (cards: AssistantPresentationResultCard[]) => {
  const actions: Array<{
    card: AssistantPresentationResultCard;
    action: NonNullable<AssistantPresentationResultCard['primaryAction']>;
  }> = [];
  const seen = new Set<string>();
  for (const card of cards) {
    const href = readOptionalNonEmptyString(card.primaryAction?.href);
    if (!href || !card.primaryAction) {
      continue;
    }
    const key = `${href}|${card.primaryAction.label}`;
    if (!seen.has(key)) {
      seen.add(key);
      actions.push({ card, action: card.primaryAction });
    }
  }
  return actions;
};

const compactResultPrimaryActionHref = (
  action: AssistantPresentationResultCard['primaryAction'],
): string | null => readOptionalNonEmptyString(action?.href) ?? null;

const renderCompactResultTextAction = (
  action: NonNullable<AssistantPresentationResultCard['primaryAction']>,
  options: {
    testId?: string;
    label?: string;
    title?: string;
  } = {},
) => {
  const href = readOptionalNonEmptyString(action.href);
  if (!href) {
    return null;
  }
  const label = options.label ?? action.label;
  const title = options.title ?? action.label;
  return (
    <a
      href={href}
      className={styles.presentationCompactResultOpenAction}
      data-testid={options.testId}
      aria-label={title}
      title={title}
      target={action.external ? '_blank' : undefined}
      rel={action.external ? 'noreferrer noopener' : undefined}
    >
      <span>{label}</span>
      {action.external ? (
        <ExternalLink size={13} className={styles.presentationActionIcon} />
      ) : (
        <ArrowUpRight size={13} className={styles.presentationActionIcon} />
      )}
    </a>
  );
};

const CompactResultListRow = ({
  group,
  index,
  uniformEyebrow,
  uniformMeta,
}: {
  group: CompactResultListGroup;
  index: number;
  uniformEyebrow: string | null;
  uniformMeta: string | null;
}) => {
  const card = group.representative;
  const groupCount = group.cards.length;
  const title = group.title;
  const preview = group.preview;
  const showPreview = preview != null && preview.toLowerCase() !== title.toLowerCase();
  const meta = group.meta;
  const showMeta = meta != null && (!uniformMeta || meta.toLowerCase() !== uniformMeta.toLowerCase());
  const eyebrow = group.eyebrow;
  const showEyebrow = eyebrow != null && (!uniformEyebrow || eyebrow.toLowerCase() !== uniformEyebrow.toLowerCase());
  const primaryActions = resolveCompactResultPrimaryActions(group.cards);
  const [firstPrimaryAction] = primaryActions;
  const firstPrimaryActionHref = compactResultPrimaryActionHref(firstPrimaryAction?.action);
  const groupBadge = groupCount > 1 ? `${groupCount} records` : group.badge;
  const hasDetailRows = group.cards.some((groupCard) => groupCard.detailRows?.length);
  const hasSecondaryActions = group.cards.some((groupCard) => groupCard.secondaryAction);
  const hasGroupedPrimaryActions = groupCount > 1 && primaryActions.length > 1;
  const showDetails = hasDetailRows || hasSecondaryActions || hasGroupedPrimaryActions;
  const detailCards = group.cards.filter((groupCard) => (
    groupCard.detailRows?.length
    || groupCard.secondaryAction
    || (
      hasGroupedPrimaryActions
      && compactResultPrimaryActionHref(groupCard.primaryAction) !== firstPrimaryActionHref
    )
  ));
  return (
    <article
      className={styles.presentationCompactResultRow}
      data-testid={`ai-result-card-${index + 1}`}
      data-group-count={groupCount}
      role="listitem"
    >
      <span className={styles.presentationCompactResultSignal} aria-hidden="true" />
      <div className={styles.presentationCompactResultMain}>
        <div className={styles.presentationCompactResultTitleRow}>
          {showEyebrow ? <span className={styles.presentationCompactResultEyebrow}>{eyebrow}</span> : null}
          <h4 className={styles.presentationCompactResultTitle}>{title}</h4>
          {groupBadge ? <span className={styles.presentationCompactResultBadge}>{groupBadge}</span> : null}
        </div>
        {showPreview || showMeta ? (
          <p className={styles.presentationCompactResultMetaLine}>
            {showMeta ? <span>{meta}{showPreview ? ':' : ''}</span> : null}
            {showPreview ? <span>{preview}</span> : null}
          </p>
        ) : null}
        {showDetails ? (
          <PresentationDisclosure
            testId={`ai-result-card-details-${index + 1}`}
            disclosureId={`compact-result-card-${group.id}-details`}
            className={styles.presentationCompactResultDetails}
            summaryClassName={styles.presentationCompactResultDetailsToggle}
            bodyClassName={styles.presentationCompactResultDetailsBody}
            summary={(
              <>
                <span>Details</span>
                <ChevronDown size={13} />
              </>
            )}
          >
            {groupCount > 1 ? (
              <p className={styles.presentationCompactResultGroupNote}>
                {groupCount} matching records share this signal.
              </p>
            ) : null}
            {detailCards.map((detailCard, detailIndex) => (
              <div key={`${group.id}-${detailCard.id}`} className={styles.presentationCompactResultDetailGroup}>
                {groupCount > 1 ? (
                  <span className={styles.presentationDetailLabel}>Record {detailIndex + 1}</span>
                ) : null}
                {detailCard.detailRows?.map((row) => (
                  <div key={`${detailCard.id}-${row.label}`} className={styles.presentationDetailRow}>
                    <span className={styles.presentationDetailLabel}>{row.label}</span>
                    <span className={styles.presentationDetailValue}>{row.value}</span>
                  </div>
                ))}
                {hasGroupedPrimaryActions
                && compactResultPrimaryActionHref(detailCard.primaryAction) !== firstPrimaryActionHref
                && detailCard.primaryAction ? (
                  <div className={styles.presentationCompactResultSecondaryAction}>
                    {renderCompactResultTextAction(detailCard.primaryAction, {
                      testId: `ai-result-card-group-primary-${index + 1}-${detailIndex + 1}`,
                      label: detailCard.primaryAction.label,
                    })}
                  </div>
                ) : null}
                {detailCard.secondaryAction ? (
                  <div className={styles.presentationCompactResultSecondaryAction}>
                    {renderActionLink(detailCard.secondaryAction, {
                      testId: `ai-result-card-secondary-${index + 1}-${detailIndex + 1}`,
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </PresentationDisclosure>
        ) : card.secondaryAction ? (
          <div className={styles.presentationCompactResultSecondaryAction}>
            {renderActionLink(card.secondaryAction, { testId: `ai-result-card-secondary-${index + 1}` })}
          </div>
        ) : null}
      </div>
      {firstPrimaryAction
        ? renderCompactResultTextAction(firstPrimaryAction.action, {
          testId: `ai-result-card-primary-${index + 1}`,
          label: groupCount > 1 ? 'Open first' : firstPrimaryAction.action.label,
          title: groupCount > 1
            ? `${firstPrimaryAction.action.label} (first of ${groupCount})`
            : firstPrimaryAction.action.label,
        })
        : null}
    </article>
  );
};

const CompactResultListSection = ({
  title,
  cards,
  exportMenu,
  pagination: paginationConfig,
}: ResultCardsSectionProps) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const uniformEyebrow = resolveUniformCardText(cards, (card) => card.eyebrow ?? card.label);
  const uniformMeta = resolveUniformCardText(cards, (card) => card.meta);
  const groups = buildCompactResultListGroups(cards, uniformEyebrow);
  const pagination = usePresentationPagination(groups, paginationConfig, sectionRef);
  const sectionMeta = [
    uniformEyebrow,
    uniformMeta,
    groups.length === cards.length
      ? `${cards.length} ${cards.length === 1 ? 'record' : 'records'}`
      : `${cards.length} records grouped into ${groups.length} ${groups.length === 1 ? 'signal' : 'signals'}`,
  ].filter(Boolean).join(' | ');
  return (
    <section
      ref={sectionRef}
      className={styles.presentationSection}
      data-testid="ai-result-cards-section"
      data-display-strategy="action_list"
      data-result-pagination={pagination.state.enabled ? 'true' : 'false'}
    >
      <PresentationSectionHeadingRow title={title} fallbackTitle="Applications needing action" exportMenu={exportMenu} />
      <div className={styles.presentationCompactResultListCard}>
        {sectionMeta ? <p className={styles.presentationCompactResultSummary}>{sectionMeta}</p> : null}
        <div className={styles.presentationCompactResultList} role="list">
          {pagination.state.visibleItems.map((group, index) => {
            const visibleIndex = pagination.state.enabled ? pagination.state.startIndex + index : index;
            return (
              <CompactResultListRow
                key={group.id}
                group={group}
                index={visibleIndex}
                uniformEyebrow={uniformEyebrow}
                uniformMeta={uniformMeta}
              />
            );
          })}
        </div>
        <div className={styles.presentationInlinePaginationFooter} data-has-pagination={pagination.state.enabled ? 'true' : 'false'}>
          <PresentationPaginationControls
            pagination={pagination.state}
            labels={resolvePresentationPaginationLabels(COMPACT_RESULT_GROUP_PAGINATION_LABELS, paginationConfig)}
            onPageIndexChange={pagination.setPageIndex}
            onPageSizeChange={pagination.setPageSize}
          />
        </div>
      </div>
    </section>
  );
};

const RESULT_CARDS_SECTION_RENDERERS: Record<ResultCardsDisplayStrategy, (props: ResultCardsSectionProps) => React.ReactNode> = {
  default: DefaultResultCardsSection,
  action_list: CompactResultListSection,
  target_gallery: TargetGalleryResultCardsSection,
};

const ResultCardsSection = ({
  displayStrategy,
  title,
  cards,
  exportMenu,
  pagination,
}: ResultCardsSectionProps & {
  displayStrategy: ResultCardsDisplayStrategy;
  pagination?: AssistantPresentationPaginationConfig | null;
}) => {
  const renderSection = RESULT_CARDS_SECTION_RENDERERS[displayStrategy] ?? DefaultResultCardsSection;
  return <>{renderSection({ title, cards, exportMenu, pagination })}</>;
};

type PresentationPaginationState<TItem> = {
  enabled: boolean;
  pageIndex: number;
  pageCount: number;
  pageSize: AssistantPresentationPageSize;
  pageSizeOptions: readonly AssistantPresentationPageSize[];
  startIndex: number;
  endIndex: number;
  visibleItems: TItem[];
  totalItems: number;
};

type PresentationPaginationLabels = {
  controlsTestId: string;
  summaryTestId: string;
  pageSizeTestIdPrefix: string;
  previousTestId: string;
  nextTestId: string;
  pageLabelTestId: string;
  itemSingular: string;
  itemPlural: string;
};

const normalizePresentationPageSize = (
  value: unknown,
  fallback: AssistantPresentationPageSize = PRESENTATION_DEFAULT_PAGE_SIZE,
): AssistantPresentationPageSize => (
  PRESENTATION_PAGE_SIZE_OPTIONS.includes(value as AssistantPresentationPageSize)
    ? value as AssistantPresentationPageSize
    : fallback
);

const normalizePresentationPageSizeOptions = (
  options: AssistantPresentationPaginationConfig['pageSizeOptions'],
): readonly AssistantPresentationPageSize[] => {
  const normalized = (options ?? PRESENTATION_PAGE_SIZE_OPTIONS)
    .map((option) => normalizePresentationPageSize(option, PRESENTATION_DEFAULT_PAGE_SIZE));
  const unique = [...new Set(normalized)].filter((option) => PRESENTATION_PAGE_SIZE_OPTIONS.includes(option));
  return unique.length > 0 ? unique : PRESENTATION_PAGE_SIZE_OPTIONS;
};

const pluralizePaginationLabel = (value: string): string => {
  if (/row/i.test(value)) return 'Rows';
  if (/result/i.test(value)) return 'Results';
  if (/signal/i.test(value)) return 'Signals';
  if (/family/i.test(value)) return 'Families';
  if (/category/i.test(value)) return 'Categories';
  if (/[^aeiou]y$/i.test(value)) return `${value.slice(0, -1)}ies`;
  return value.endsWith('s') ? value : `${value}s`;
};

const resolvePresentationPaginationLabels = (
  labels: PresentationPaginationLabels,
  config?: AssistantPresentationPaginationConfig | null,
): PresentationPaginationLabels => {
  const itemSingular = readOptionalNonEmptyString(config?.itemLabel) ?? labels.itemSingular;
  return {
    ...labels,
    itemSingular,
    itemPlural: pluralizePaginationLabel(itemSingular),
  };
};

const resolvePresentationPaginationState = <TItem,>({
  items,
  pageIndex,
  pageSize,
  config,
}: {
  items: TItem[];
  pageIndex: number;
  pageSize: AssistantPresentationPageSize;
  config?: AssistantPresentationPaginationConfig | null;
}): PresentationPaginationState<TItem> => {
  const pageSizeOptions = normalizePresentationPageSizeOptions(config?.pageSizeOptions);
  const normalizedPageSize = pageSizeOptions.includes(pageSize) ? pageSize : pageSizeOptions[0] ?? PRESENTATION_DEFAULT_PAGE_SIZE;
  const threshold = Math.max(1, Math.floor(Number(config?.threshold ?? PRESENTATION_PAGINATION_ITEM_THRESHOLD)));
  const mode = config?.mode ?? 'auto';
  const enabled = mode === 'disabled'
    ? false
    : mode === 'always'
      ? items.length > 0
      : items.length > threshold;
  const pageCount = enabled ? Math.max(1, Math.ceil(items.length / normalizedPageSize)) : 1;
  const safePageIndex = enabled ? Math.min(Math.max(pageIndex, 0), pageCount - 1) : 0;
  const startIndex = enabled ? safePageIndex * normalizedPageSize : 0;
  const endIndex = enabled ? Math.min(startIndex + normalizedPageSize, items.length) : items.length;

  return {
    enabled,
    pageIndex: safePageIndex,
    pageCount,
    pageSize: normalizedPageSize,
    pageSizeOptions,
    startIndex,
    endIndex,
    visibleItems: enabled ? items.slice(startIndex, endIndex) : items,
    totalItems: items.length,
  };
};

const PresentationPaginationControls = <TItem,>({
  pagination,
  labels,
  onPageIndexChange,
  onPageSizeChange,
}: {
  pagination: PresentationPaginationState<TItem>;
  labels: PresentationPaginationLabels;
  onPageIndexChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: AssistantPresentationPageSize) => void;
}) => {
  if (!pagination.enabled) {
    return null;
  }

  const canGoPrevious = pagination.pageIndex > 0;
  const canGoNext = pagination.pageIndex + 1 < pagination.pageCount;
  const itemLabel = pagination.totalItems === 1 ? labels.itemSingular : labels.itemPlural;
  const rangeText = `${itemLabel} ${pagination.startIndex + 1}-${pagination.endIndex} of ${pagination.totalItems}`;

  return (
    <div className={styles.presentationTablePagination} data-testid={labels.controlsTestId}>
      <span
        className={styles.presentationTablePaginationSummary}
        data-testid={labels.summaryTestId}
        aria-live="polite"
      >
        {rangeText}
      </span>
      <div className={styles.presentationTablePageSizeGroup} aria-label={`${labels.itemPlural} per page`}>
        {pagination.pageSizeOptions.map((size) => (
          <button
            key={size}
            type="button"
            className={styles.presentationTablePageSizeButton}
            data-testid={`${labels.pageSizeTestIdPrefix}-${size}`}
            data-active={pagination.pageSize === size ? 'true' : 'false'}
            aria-pressed={pagination.pageSize === size}
            onClick={() => onPageSizeChange(size)}
          >
            {size}
          </button>
        ))}
      </div>
      <div className={styles.presentationTablePaginationButtons}>
        <button
          type="button"
          className={styles.presentationTablePaginationButton}
          data-testid={labels.previousTestId}
          aria-label="Previous page"
          title="Previous page"
          disabled={!canGoPrevious}
          onClick={() => onPageIndexChange(pagination.pageIndex - 1)}
        >
          <ChevronLeft size={14} aria-hidden="true" />
        </button>
        <span className={styles.presentationTablePaginationPage} data-testid={labels.pageLabelTestId}>
          {pagination.pageIndex + 1} / {pagination.pageCount}
        </span>
        <button
          type="button"
          className={styles.presentationTablePaginationButton}
          data-testid={labels.nextTestId}
          aria-label="Next page"
          title="Next page"
          disabled={!canGoNext}
          onClick={() => onPageIndexChange(pagination.pageIndex + 1)}
        >
          <ChevronRight size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const scrollPaginationSectionIntoView = (target: Element | null): void => {
  if (!target || typeof window === 'undefined') {
    return;
  }
  window.requestAnimationFrame(() => {
    if (typeof target.scrollIntoView !== 'function') {
      return;
    }
    target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
  });
};

const usePresentationPagination = <TItem,>(
  items: TItem[],
  config?: AssistantPresentationPaginationConfig | null,
  scrollTargetRef?: React.RefObject<Element | null>,
) => {
  const initialPageSize = normalizePresentationPageSize(config?.pageSize, PRESENTATION_DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndexState] = useState(0);
  const [pageSize, setPageSizeState] = useState<AssistantPresentationPageSize>(initialPageSize);
  const state = useMemo(
    () => resolvePresentationPaginationState({ items, pageIndex, pageSize, config }),
    [config, items, pageIndex, pageSize],
  );
  const scrollToSection = useCallback(() => {
    scrollPaginationSectionIntoView(scrollTargetRef?.current ?? null);
  }, [scrollTargetRef]);
  const setPageIndex = useCallback((nextPageIndex: number) => {
    setPageIndexState(nextPageIndex);
    scrollToSection();
  }, [scrollToSection]);
  const setPageSize = useCallback((nextPageSize: AssistantPresentationPageSize) => {
    setPageSizeState(nextPageSize);
    setPageIndexState(0);
    scrollToSection();
  }, [scrollToSection]);

  useEffect(() => {
    setPageIndexState(0);
  }, [items.length]);

  useEffect(() => {
    setPageSizeState(normalizePresentationPageSize(config?.pageSize, PRESENTATION_DEFAULT_PAGE_SIZE));
  }, [config?.pageSize]);

  return {
    state,
    setPageIndex,
    setPageSize,
  };
};

type AssistantPresentationTableScrollPolicy = 'fit' | 'horizontal';

const tableCellPlainText = (cell: string | AssistantPresentationTableCell | undefined): string => {
  if (cell && typeof cell === 'object') {
    return [cell.text, cell.detail].map(normalizeExportText).filter(Boolean).join(' ');
  }
  return normalizeExportText(cell);
};

const tableCellHasDetail = (cell: string | AssistantPresentationTableCell | undefined): boolean =>
  Boolean(cell && typeof cell === 'object' && readOptionalNonEmptyString(cell.detail));

const resolveTableScrollPolicy = ({
  columns,
  rows,
}: {
  columns: AssistantPresentationTableColumn[];
  rows: AssistantPresentationTableRow[];
}): AssistantPresentationTableScrollPolicy => {
  if (columns.length <= 2) {
    return 'fit';
  }
  if (columns.length >= 4) {
    return 'horizontal';
  }
  const hasDenseCell = rows.some((row) => row.cells.some((cell) => (
    tableCellHasDetail(cell) || tableCellPlainText(cell).length > 24
  )));
  return hasDenseCell ? 'horizontal' : 'fit';
};

const TableSection = ({
  sectionId,
  title,
  columns,
  rows,
  footerText,
  footerAction,
  exportMenu,
  pagination: paginationConfig,
}: {
  sectionId?: string | null;
  title?: string | null;
  columns: AssistantPresentationTableColumn[];
  rows: AssistantPresentationTableRow[];
  footerText?: string | null;
  footerAction?: AssistantPresentationActionLink | null;
  exportMenu?: React.ReactNode;
  pagination?: AssistantPresentationPaginationConfig | null;
}) => {
  const hasHeading = typeof title === 'string' ? title.trim().length > 0 : title != null;
  const sectionRef = useRef<HTMLElement | null>(null);
  const pagination = usePresentationPagination(rows, paginationConfig, sectionRef);
  const tableExportArtifacts = useMemo(
    () => collectTableDataExportArtifacts({ sectionId, title, columns, rows }),
    [columns, rows, sectionId, title],
  );

  const hasFooter = Boolean(footerText || footerAction || pagination.state.enabled);
  const tableExportMenu = tableExportArtifacts.length > 0 ? (
    <AssistantPresentationExportMenu
      items={tableExportArtifacts}
      testId="ai-table-export-menu"
      buttonTestId="ai-table-export-menu-button"
      listTestId="ai-table-export-menu-list"
      menuItemTestIdPrefix="ai-table-export-menu-item"
      genericActionTestIdPrefix="ai-table-export-artifact-action"
      label="Export table data"
      dataExportSurface="assistant-table-data"
    />
  ) : null;
  const tableScrollPolicy = resolveTableScrollPolicy({ columns, rows });

  return (
    <section
      ref={sectionRef}
      className={styles.presentationSection}
      data-testid="ai-table-section"
      data-table-pagination={pagination.state.enabled ? 'true' : 'false'}
    >
      <PresentationSectionHeadingRow
        title={hasHeading ? title : null}
        exportMenu={composeSectionActions(tableExportMenu, exportMenu)}
      />
      <div
        className={styles.presentationTableCard}
        data-table-density={pagination.state.enabled ? 'paginated' : 'standard'}
        data-table-columns={columns.length}
        data-table-scroll-policy={tableScrollPolicy}
      >
        <div className={styles.presentationTableScroll}>
          <table className={styles.presentationTable}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.id}
                    className={styles.presentationTableHeading}
                    data-align={column.align ?? 'left'}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagination.state.visibleItems.map((row, rowIndex) => {
                const absoluteRowIndex = pagination.state.enabled ? pagination.state.startIndex + rowIndex : rowIndex;

                return (
                  <tr key={row.id ?? `row-${absoluteRowIndex + 1}`} className={styles.presentationTableRow}>
                    {columns.map((column, columnIndex) => (
                      <td
                        key={`${row.id ?? absoluteRowIndex}-${column.id}`}
                        className={styles.presentationTableCell}
                        data-align={column.align ?? 'left'}
                        data-label={column.label}
                        data-column-id={column.id}
                      >
                        {renderTableCellContent(row.cells[columnIndex])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasFooter ? (
          <div className={styles.presentationTableFooter} data-has-pagination={pagination.state.enabled ? 'true' : 'false'}>
            {footerText || footerAction ? (
              <div className={styles.presentationTableFooterMeta}>
                {footerText ? (
                  <span className={styles.presentationTableFooterText}>
                    {renderTableFooterText(footerText)}
                  </span>
                ) : <span />}
                {footerAction ? renderTableFooterAction(footerAction) : null}
              </div>
            ) : null}
            <PresentationPaginationControls
              pagination={pagination.state}
              labels={resolvePresentationPaginationLabels(TABLE_PAGINATION_LABELS, paginationConfig)}
              onPageIndexChange={pagination.setPageIndex}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

const renderTableFooterText = (footerText: string): React.ReactNode => {
  const normalized = footerText.replace(/\s+—\s+/g, ' — ').trim();
  if (!normalized.includes('View all')) {
    return normalized;
  }
  const prefix = normalized.replace(/\s*—\s*View all\s*$/i, '').trim();
  return (
    <>
      <span>{prefix}</span>
      <span>{' — '}</span>
      <button type="button" className={styles.presentationTableFooterInlineAction}>
        View all
      </button>
    </>
  );
};

const resolveChartFillClass = (tone: AssistantPresentationChartItem['tone']): string => {
  switch (tone) {
    case 'accent':
      return styles.presentationChartBarAccent;
    case 'neutral':
      return styles.presentationChartBarNeutral;
    default:
      return styles.presentationChartBarPrimary;
  }
};

const ChartSection = ({
  sectionId,
  title,
  chartTitle,
  items,
  maxValue,
  exportMenu,
}: {
  sectionId?: string | null;
  title?: string | null;
  chartTitle?: string | null;
  items: AssistantPresentationChartItem[];
  maxValue?: number | null;
  exportMenu?: React.ReactNode;
}) => {
  const normalizedMaxValue = Math.max(
    maxValue ?? 0,
    ...items.map((item) => item.value),
    1,
  );
  const hasHeading = typeof title === 'string' ? title.trim().length > 0 : title != null;
  const itemCount = Math.max(items.length, 1);
  const isDenseChart = items.length > 5;
  const rowMinWidthPx = isDenseChart ? Math.max(232, itemCount * 58) : 0;
  const rowStyle = {
    '--ai-presentation-chart-min-width': `${rowMinWidthPx}px`,
  } as React.CSSProperties;

  return (
    <section
      id={sectionId ?? undefined}
      className={styles.presentationSection}
      data-testid="ai-chart-section"
      data-chart-renderer="compact-bars"
    >
      <PresentationSectionHeadingRow title={hasHeading ? title : null} exportMenu={exportMenu} />
      <article className={styles.presentationChartCard}>
        {chartTitle ? <p className={styles.presentationChartTitle}>{chartTitle}</p> : null}
        <div
          className={styles.presentationChartViewport}
          data-testid="ai-chart-viewport"
          data-chart-item-count={items.length}
        >
          <div
            className={styles.presentationChartRow}
            data-density={isDenseChart ? 'scroll' : 'fit'}
            style={rowStyle}
          >
            {items.map((item, index) => {
              const heightPercent = Math.max(10, Math.round((item.value / normalizedMaxValue) * 100));
              return (
                <div
                  key={item.id}
                  className={styles.presentationChartColumn}
                  data-testid={`ai-chart-bar-${index + 1}`}
                  aria-label={`${item.label}: ${item.value}`}
                >
                  <div className={styles.presentationChartTrack}>
                    <div
                      className={`${styles.presentationChartBar} ${resolveChartFillClass(item.tone)}`}
                      style={{ height: `${heightPercent}%` }}
                    />
                  </div>
                  <span className={styles.presentationChartLabel} title={item.label}>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </article>
    </section>
  );
};

const resolveConfirmationToneClass = (tone: AssistantPresentationConfirmationTone | undefined): string => {
  switch (tone) {
    case 'warning':
      return styles.presentationConfirmationWarning;
    case 'info':
      return styles.presentationConfirmationInfo;
    default:
      return styles.presentationConfirmationSuccess;
  }
};

const resolveConfirmationIcon = (tone: AssistantPresentationConfirmationTone | undefined) => {
  if (tone === 'warning') {
    return <TriangleAlert size={16} className={styles.presentationConfirmationIconWarning} />;
  }
  if (tone === 'info') {
    return <CircleAlert size={16} className={styles.presentationConfirmationIconInfo} />;
  }
  return <CircleCheckBig size={16} className={styles.presentationConfirmationIconSuccess} />;
};

const ConfirmationSection = ({
  title,
  tone,
  titleText,
  summary,
  meta,
  exportMenu,
}: {
  title?: string | null;
  tone?: AssistantPresentationConfirmationTone;
  titleText: string;
  summary: string;
  meta?: string | null;
  exportMenu?: React.ReactNode;
}) => {
  const hasHeading = typeof title === 'string' ? title.trim().length > 0 : title != null;
  return (
  <section className={styles.presentationSection} data-testid="ai-confirmation-section">
    <PresentationSectionHeadingRow title={hasHeading ? title : null} exportMenu={exportMenu} />
    <article className={`${styles.presentationConfirmationCard} ${resolveConfirmationToneClass(tone)}`}>
      <div className={styles.presentationConfirmationHeader}>
        <span className={styles.presentationConfirmationIcon}>{resolveConfirmationIcon(tone)}</span>
        <div className={styles.presentationConfirmationContent}>
          <p className={styles.presentationCardTitle}>{titleText}</p>
          <p className={styles.presentationCardPreview}>{summary}</p>
          {meta ? <p className={styles.presentationCardMeta}>{meta}</p> : null}
        </div>
      </div>
    </article>
  </section>
  );
};

const MetricGridSection = ({
  title,
  items,
  exportMenu,
}: {
  title?: string | null;
  items: AssistantPresentationMetricItem[];
  exportMenu?: React.ReactNode;
}) => {
  const hasHeading = typeof title === 'string' ? title.trim().length > 0 : title != null;
  const isKeyNumbers = typeof title === 'string' && title.trim().toLowerCase() === 'key numbers';
  return (
  <section
    className={styles.presentationSection}
    data-testid="ai-metric-grid-section"
    data-ai-metric-count={items.length}
    data-ai-ui-deviation={isKeyNumbers ? 'centered-key-number-summary-v1' : undefined}
  >
    <PresentationSectionHeadingRow title={hasHeading ? title : null} exportMenu={exportMenu} />
    <div className={styles.presentationMetricGrid}>
      {items.map((item, index) => (
        <article
          key={item.id}
          className={`${styles.presentationMetricCard} ${resolveMetricToneClass(item.tone)}`}
          data-testid={`ai-metric-card-${index + 1}`}
        >
          <p className={styles.presentationMetricLabel}>{item.label}</p>
          <p className={styles.presentationMetricValue}>{item.value}</p>
          {item.detail ? <p className={styles.presentationMetricDetail}>{item.detail}</p> : null}
        </article>
      ))}
    </div>
  </section>
  );
};

const ChecklistSection = ({
  title,
  items,
  exportMenu,
  pagination: paginationConfig,
}: {
  title?: string | null;
  items: AssistantPresentationChecklistItem[];
  exportMenu?: React.ReactNode;
  pagination?: AssistantPresentationPaginationConfig | null;
}) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const pagination = usePresentationPagination(items, paginationConfig, sectionRef);

  return (
    <section
      ref={sectionRef}
      className={styles.presentationSection}
      data-testid="ai-checklist-section"
      data-checklist-pagination={pagination.state.enabled ? 'true' : 'false'}
    >
      <PresentationSectionHeadingRow title={title} fallbackTitle="Checklist" exportMenu={exportMenu} />
      <div className={styles.presentationChecklistList}>
        {pagination.state.visibleItems.map((item, index) => {
          const absoluteIndex = pagination.state.enabled ? pagination.state.startIndex + index : index;

          return (
            <article
              key={item.id}
              className={styles.presentationChecklistItem}
              data-testid={`ai-checklist-item-${index + 1}`}
              data-item-index={absoluteIndex + 1}
            >
              <div className={styles.presentationChecklistIcon}>
                {resolveChecklistIcon(item.status)}
              </div>
              <div className={styles.presentationChecklistContent}>
                <p className={styles.presentationListTitle}>{item.label}</p>
                {item.detail ? <p className={styles.presentationListPreview}>{item.detail}</p> : null}
              </div>
            </article>
          );
        })}
      </div>
      <div className={styles.presentationInlinePaginationFooter} data-has-pagination={pagination.state.enabled ? 'true' : 'false'}>
        <PresentationPaginationControls
          pagination={pagination.state}
          labels={resolvePresentationPaginationLabels(CHECKLIST_PAGINATION_LABELS, paginationConfig)}
          onPageIndexChange={pagination.setPageIndex}
          onPageSizeChange={pagination.setPageSize}
        />
      </div>
    </section>
  );
};

const ArtifactsSection = ({
  title,
  items,
  exportMenu,
  onArtifactAction,
}: {
  title?: string | null;
  items: AssistantPresentationArtifactItem[];
  exportMenu?: React.ReactNode;
  onArtifactAction?: (
    item: AssistantPresentationArtifactItem,
    action: IntellioAssistantArtifactActionDescriptor,
  ) => void;
}) => (
  <section
    className={styles.presentationSection}
    data-testid="ai-artifacts-section"
  >
    <PresentationSectionHeadingRow title={title} fallbackTitle="Artifacts & workspaces" exportMenu={exportMenu} />
    <div className={styles.presentationArtifactGrid} data-testid="ai-artifact-card-list">
      {items.map((item, index) => {
        const downloadable = isDownloadablePresentationArtifact(item);
        const artifactActions = item.actions?.filter((action) => !action.disabledReason) ?? [];
        const hasArtifactActions = artifactActions.length > 0 || downloadable;
        const artifactActionCount = artifactActions.length + (downloadable ? 1 : 0);
        return (
          <article
            key={item.id}
            className={styles.presentationArtifactCard}
            data-testid={`ai-artifact-card-${index + 1}`}
            data-source-ref={item.sourceRef ?? undefined}
            data-artifact-source-ref={item.sourceRef ?? undefined}
          >
            <div className={styles.presentationResultHeader}>
              <div className={styles.presentationArtifactTitleCluster}>
                <span className={styles.presentationArtifactIconTile} aria-hidden="true">
                  <FileText size={18} />
                </span>
                <div>
                  <h4 className={styles.presentationCardTitle}>{item.label}</h4>
                  {item.meta ? <p className={styles.presentationCardMeta}>{item.meta}</p> : null}
                </div>
              </div>
              {item.badge ? <span className={styles.presentationCardBadge}>{item.badge}</span> : null}
            </div>
            {item.preview ? <p className={styles.presentationCardPreview}>{item.preview}</p> : null}
            {hasArtifactActions ? (
              <div
                className={styles.presentationActionRow}
                style={{
                  '--assistant-artifact-action-count': String(Math.max(1, artifactActionCount)),
                  gridTemplateColumns: `repeat(${Math.max(1, artifactActionCount)}, minmax(0, 1fr))`,
                } as React.CSSProperties}
              >
                {artifactActions.map((action, actionIndex) => (
                  <button
                    key={action.id}
                    type="button"
                    className={actionIndex === 0 ? styles.presentationPrimaryAction : styles.presentationSecondaryAction}
                    data-testid={`ai-artifact-card-generic-action-${index + 1}-${actionIndex + 1}`}
                    data-artifact-action-kind={action.kind}
                    data-href={action.href ?? undefined}
                    data-source-ref={action.sourceRef ?? item.sourceRef ?? undefined}
                    data-artifact-source-ref={action.sourceRef ?? item.sourceRef ?? undefined}
                    onClick={() => onArtifactAction?.(item, action)}
                  >
                    {action.label}
                  </button>
                ))}
                {downloadable ? (
                  <button
                    type="button"
                    className={styles.presentationSecondaryAction}
                    data-testid={`ai-artifact-card-action-${index + 1}`}
                    aria-label={item.actionLabel ?? `Download ${resolveArtifactFormat(item)}`}
                    title={item.actionLabel ?? `Download ${resolveArtifactFormat(item)}`}
                    onClick={() => downloadPresentationArtifact(item)}
                  >
                    <Download size={14} />
                    <span>{item.actionLabel ?? `Download ${resolveArtifactFormat(item)}`}</span>
                  </button>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  </section>
);

const MultiArtifactBrowser = ({
  preferredArtifactType,
  tabs,
  renderTabSection,
}: {
  preferredArtifactType?: AssistantPresentationSectionType | null;
  tabs: MultiArtifactBrowserTab[];
  renderTabSection: (tab: MultiArtifactBrowserTab) => React.ReactNode;
}) => {
  const browserDomId = React.useId();
  const resolveInitialTabId = () =>
    tabs.find((tab) => tab.section.type === preferredArtifactType)?.id
    ?? tabs[0]?.id
    ?? '';
  const [activeTabId, setActiveTabId] = useState(resolveInitialTabId);

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTabId)) {
      setActiveTabId(resolveInitialTabId());
    }
  }, [activeTabId, preferredArtifactType, tabs]);

  if (tabs.length === 0) {
    return null;
  }

  if (tabs.length === 1) {
    return <>{renderTabSection(tabs[0])}</>;
  }

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
  const activePanelId = `${browserDomId}-${activeTab.id}-panel`;
  const activeTabDomId = `${browserDomId}-${activeTab.id}`;

  return (
    <section
      className={styles.presentationMultiArtifactBrowser}
      data-testid="ai-multi-artifact-browser"
      data-assistant-presentation-section-type="multi_artifact_browser"
      data-tab-count={tabs.length}
    >
      <div
        className={styles.presentationMultiArtifactTabList}
        data-testid="ai-multi-artifact-tablist"
        role="tablist"
        aria-label="Response artifacts"
      >
        {tabs.map((tab, index) => {
          const selected = tab.id === activeTab.id;
          const tabDomId = `${browserDomId}-${tab.id}`;
          return (
            <button
              key={tab.id}
              id={tabDomId}
              type="button"
              className={styles.presentationMultiArtifactTab}
              data-testid={`ai-multi-artifact-tab-${index + 1}`}
              data-section-type={tab.section.type}
              data-active={selected ? 'true' : 'false'}
              role="tab"
              aria-selected={selected}
              aria-controls={selected ? activePanelId : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        id={activePanelId}
        className={styles.presentationMultiArtifactPanel}
        data-testid="ai-multi-artifact-panel"
        data-active-tab-type={activeTab.section.type}
        role="tabpanel"
        aria-labelledby={activeTabDomId}
      >
        {renderTabSection(activeTab)}
      </div>
    </section>
  );
};

const TaskProposalSection = ({
  title,
  proposal,
  onStart,
  onAskInChat,
  onDismiss,
}: {
  title?: string | null;
  proposal: AssistantPresentationTaskProposal;
  onStart?: (proposalId: string) => void;
  onAskInChat?: (proposalId: string) => void;
  onDismiss?: (proposalId: string) => void;
}) => {
  const hasActions = Boolean(onStart || onAskInChat || onDismiss);
  return (
    <section className={styles.presentationSection} data-testid="ai-task-launch-card">
      <div className={styles.presentationSectionHeadingRow}>
        <span className={styles.presentationSectionEyebrow}>{title ?? 'Task proposal'}</span>
      </div>
      <article className={styles.presentationTaskCard}>
        <div className={styles.presentationResultHeader}>
          <div>
            <h4 className={styles.presentationCardTitle}>{proposal.title}</h4>
            {proposal.question ? <p className={styles.presentationCardMeta}>{proposal.question}</p> : null}
          </div>
          <span className={styles.presentationCardBadge}>Proposal</span>
        </div>
        <p className={styles.presentationCardPreview}>{proposal.summary}</p>
        {hasActions ? (
          <div className={styles.presentationActionRow}>
            {onStart ? (
              <button
                type="button"
                className={styles.presentationPrimaryAction}
                data-testid="ai-task-launch-start"
                onClick={() => onStart(proposal.proposalId)}
              >
                {proposal.startLabel ?? 'Start task'}
              </button>
            ) : null}
            {onAskInChat ? (
              <button
                type="button"
                className={styles.presentationSecondaryAction}
                data-testid="ai-task-launch-chat"
                onClick={() => onAskInChat(proposal.proposalId)}
              >
                {proposal.askInChatLabel ?? 'Ask in chat instead'}
              </button>
            ) : null}
            {onDismiss ? (
              <button
                type="button"
                className={styles.presentationSecondaryAction}
                data-testid="ai-task-launch-dismiss"
                onClick={() => onDismiss(proposal.proposalId)}
              >
                {proposal.dismissLabel ?? 'Dismiss'}
              </button>
            ) : null}
          </div>
        ) : null}
      </article>
    </section>
  );
};

const TaskThreadSection = ({ title, thread }: { title?: string | null; thread: AssistantPresentationTaskThread }) => (
  <section
    className={styles.presentationSection}
    data-testid="ai-task-thread-status"
    data-ai-task-thread-id={thread.threadId}
  >
    <div className={styles.presentationSectionHeadingRow}>
      <span className={styles.presentationSectionEyebrow}>{title ?? 'Background task'}</span>
    </div>
    <article className={styles.presentationTaskCard}>
      <div className={styles.presentationResultHeader}>
        <div>
          <h4 className={styles.presentationCardTitle}>{thread.title}</h4>
          <p className={styles.presentationCardMeta}>{thread.stateLabel}</p>
        </div>
        <span className={styles.presentationCardBadge}>{thread.stateLabel}</span>
      </div>
      {thread.statusMessage ? <p className={styles.presentationCardPreview}>{thread.statusMessage}</p> : null}
      {typeof thread.artifactCount === 'number' ? (
        <p className={styles.presentationCardMeta}>
          {thread.artifactCount} artifact{thread.artifactCount === 1 ? '' : 's'}
        </p>
      ) : null}
      {thread.detailsSummary || thread.events?.length ? (
        <PresentationDisclosure
          testId="ai-task-thread-rollup-details"
          disclosureId={`task-thread-${thread.threadId}-details`}
          className={styles.presentationDetails}
          summaryClassName={styles.presentationDetailsSummary}
          bodyClassName={styles.presentationDetailsBody}
          summary={(
            <>
              <span>Task details</span>
              <ChevronDown size={14} />
            </>
          )}
        >
            {thread.detailsSummary ? <p className={styles.presentationCardPreview}>{thread.detailsSummary}</p> : null}
            {thread.events?.map((event, index) => (
              <div key={event.id ?? `${thread.threadId}-${index + 1}`} className={styles.presentationTimelineRow}>
                <span className={styles.presentationDetailLabel}>{event.label}</span>
                <div>
                  {event.summary ? <p className={styles.presentationDetailValue}>{event.summary}</p> : null}
                  {event.content ? <p className={styles.presentationTimelineContent}>{event.content}</p> : null}
                </div>
              </div>
            ))}
            {thread.degradedReason ? (
              <p className={styles.presentationWarningText}>{thread.degradedReason}</p>
            ) : null}
        </PresentationDisclosure>
      ) : null}
    </article>
  </section>
);

type ConsumerSourceCategory =
  | 'admissions-applications'
  | 'admissions-notes'
  | 'admissions-timeline'
  | 'admissions-calendar'
  | 'financial-aid'
  | 'settings'
  | 'student-family'
  | 'generic';

type ConsumerSourceItem = {
  id: string;
  label: string;
  meta: string | null;
  preview: string | null;
  title: string | null;
  badge: string | null;
  category: ConsumerSourceCategory;
  count: number | null;
  source: AssistantPresentationSourceItem;
  sourceIndex: number;
};

type ConsumerSourceCopy = {
  id: string;
  label: string;
  meta: (count: number | null) => string;
  preview: string;
  badge: string;
};

const compactSourceText = (value: unknown): string =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

const sourceTextIncludes = (value: string, pattern: RegExp): boolean =>
  pattern.test(value.toLowerCase());

const readSourceCount = (source: AssistantPresentationSourceItem): number | null => {
  const text = [
    source.label,
    source.meta ?? '',
    source.preview ?? '',
    source.title ?? '',
  ].map(compactSourceText).join(' ');
  const countMatch = text.match(/(?:rows?|count|applications?|records?)[:\s-]+(\d{1,6})\b/i)
    ?? text.match(/\b(\d{1,6})\s+(?:application|record|row)s?\b/i);
  const count = Number(countMatch?.[1] ?? NaN);
  return Number.isFinite(count) && count > 0 ? count : null;
};

const resolveConsumerSourceCategory = (
  source: AssistantPresentationSourceItem,
): ConsumerSourceCategory => {
  const signalText = compactSourceText(`${source.label} ${source.meta ?? ''}`).toLowerCase();
  const allText = compactSourceText(`${signalText} ${source.preview ?? ''} ${source.title ?? ''}`).toLowerCase();

  if (sourceTextIncludes(signalText, /\bnotes?\b|admissions\.notes/)) {
    return 'admissions-notes';
  }
  if (sourceTextIncludes(signalText, /timeline|eventkey/)) {
    return 'admissions-timeline';
  }
  if (sourceTextIncludes(signalText, /calendar|interview|visit/)) {
    return 'admissions-calendar';
  }
  if (sourceTextIncludes(allText, /financial[-\s]?aid|award|appeal|document follow-up/)) {
    return 'financial-aid';
  }
  if (sourceTextIncludes(allText, /settings|moduleconfig|division capacity/)) {
    return 'settings';
  }
  if (sourceTextIncludes(allText, /students?[-\s]?famil|family profile|guardian|parent portal/)) {
    return 'student-family';
  }
  if (sourceTextIncludes(signalText, /listapplications|getapplication|applications|routecache|admissions\.applications/)) {
    return 'admissions-applications';
  }
  return 'generic';
};

const friendlyGenericSourceLabel = (
  source: AssistantPresentationSourceItem,
  index: number,
): string => {
  const label = compactSourceText(source.label);
  if (
    !label
    || /^source\s+\d+$/i.test(label)
    || /[.:]/.test(label)
    || /\b(?:list|get|routecache|debug|sourceledger)\b/i.test(label)
  ) {
    return `Supporting source ${index + 1}`;
  }
  return label;
};

const SOURCE_CATEGORY_COPY: Partial<Record<ConsumerSourceCategory, ConsumerSourceCopy>> = {
  'admissions-applications': {
    id: 'source-admissions-dashboard-data',
    label: 'Admissions dashboard data',
    meta: (count) => count != null ? `${count} application rows` : 'Current dashboard source',
    preview: 'Used the visible admissions application records authorized for this dashboard.',
    badge: 'Live',
  },
  'admissions-notes': {
    id: 'source-admissions-notes',
    label: 'Admissions notes',
    meta: () => 'Attached notes',
    preview: 'Used notes available on the admissions records included in this answer.',
    badge: 'Live',
  },
  'admissions-timeline': {
    id: 'source-admissions-timeline',
    label: 'Admissions timeline',
    meta: () => 'Recent activity',
    preview: 'Used timeline activity available for the relevant admissions records.',
    badge: 'Live',
  },
  'admissions-calendar': {
    id: 'source-admissions-calendar',
    label: 'Admissions calendar',
    meta: () => 'Scheduled events',
    preview: 'Used visit and interview events available to the admissions dashboard.',
    badge: 'Live',
  },
  'financial-aid': {
    id: 'source-financial-aid-review-data',
    label: 'Financial aid review data',
    meta: (count) => count != null ? `${count} records` : 'Current review source',
    preview: 'Used financial aid queue, document, flag, or appeal records available for review.',
    badge: 'Live',
  },
  settings: {
    id: 'source-settings-configuration',
    label: 'Settings configuration',
    meta: () => 'Current settings',
    preview: 'Used saved configuration values that affect this answer.',
    badge: 'Config',
  },
  'student-family': {
    id: 'source-student-family-records',
    label: 'Student and family records',
    meta: (count) => count != null ? `${count} records` : 'Current records',
    preview: 'Used student, family, or guardian records available to this route.',
    badge: 'Live',
  },
};

const buildConsumerSourceItem = (
  source: AssistantPresentationSourceItem,
  sourceIndex: number,
): ConsumerSourceItem => {
  const category = resolveConsumerSourceCategory(source);
  const count = readSourceCount(source);
  const copy = SOURCE_CATEGORY_COPY[category];
  if (copy) {
    return {
      id: copy.id,
      label: copy.label,
      meta: copy.meta(count),
      preview: copy.preview,
      title: copy.label,
      badge: copy.badge,
      category,
      count,
      source,
      sourceIndex,
    };
  }
  const label = friendlyGenericSourceLabel(source, sourceIndex);
  const preview = compactSourceText(source.preview) || null;
  const meta = compactSourceText(source.meta) || null;
  return {
    id: compactSourceText(source.id) || `source-${sourceIndex + 1}`,
    label,
    meta,
    preview,
    title: [label, meta, preview].filter(Boolean).join('\n') || label,
    badge: null,
    category,
    count,
    source,
    sourceIndex,
  };
};

const mergeConsumerSourceItems = (
  items: ConsumerSourceItem[],
): ConsumerSourceItem[] => {
  const merged = new Map<string, ConsumerSourceItem>();
  for (const item of items) {
    const key = item.category === 'generic'
      ? `${item.label}::${item.meta ?? ''}::${item.preview ?? ''}`.toLowerCase()
      : item.category;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, item);
      continue;
    }
    if (existing.count == null && item.count != null) {
      merged.set(key, {
        ...existing,
        count: item.count,
        meta: item.meta,
      });
    }
  }
  return Array.from(merged.values()).slice(0, 6);
};

const buildConsumerSourceItems = (
  items: AssistantPresentationSourceItem[],
): ConsumerSourceItem[] =>
  mergeConsumerSourceItems(items.map((item, index) => buildConsumerSourceItem(item, index)));

const SourcesSection = ({
  title,
  items,
  onSourceClick,
  getSourceTooltip,
  inline = false,
}: {
  title?: string | null;
  items: AssistantPresentationSourceItem[];
  onSourceClick?: (source: AssistantPresentationSourceItem, index: number) => void;
  getSourceTooltip?: (source: AssistantPresentationSourceItem, index: number) => string | undefined;
  inline?: boolean;
}) => {
  const consumerSources = buildConsumerSourceItems(items);
  const sourceItems = consumerSources.map((source) => {
    const cardBody = (
      <>
        <div className={styles.presentationListIcon}>
          <FileText size={14} />
        </div>
        <div className={styles.presentationListContent}>
          <div className={styles.presentationListTitleRow}>
            <p className={styles.presentationListTitle}>{source.label}</p>
            {source.badge ? <span className={styles.presentationSourceBadge}>{source.badge}</span> : null}
          </div>
          {source.meta ? <p className={styles.presentationListMeta}>{source.meta}</p> : null}
          {source.preview ? <p className={styles.presentationListPreview}>{source.preview}</p> : null}
        </div>
      </>
    );

    return onSourceClick ? (
      <button
        key={source.id}
        type="button"
        className={`${styles.presentationListCard} ${styles.presentationSourceCard}`}
        data-testid={`source-badge-${source.sourceIndex + 1}`}
        onClick={() => onSourceClick(source.source, source.sourceIndex)}
        title={source.title ?? getSourceTooltip?.(source.source, source.sourceIndex)}
      >
        {cardBody}
      </button>
    ) : (
      <article
        key={source.id}
        className={`${styles.presentationListCard} ${styles.presentationSourceCard}`}
        data-testid={`source-badge-${source.sourceIndex + 1}`}
        title={source.title ?? getSourceTooltip?.(source.source, source.sourceIndex)}
      >
        {cardBody}
      </article>
    );
  });

  if (inline) {
    return (
      <section className={styles.presentationSection} data-testid="ai-sources-section">
        <PresentationSectionHeadingRow title={title} fallbackTitle={`Sources (${consumerSources.length})`} />
        <div className={styles.presentationDrawerList} data-testid="ai-assistant-message-sources">
          {sourceItems}
        </div>
      </section>
    );
  }

  return (
    <PresentationDisclosure
      testId="ai-sources-section"
      disclosureId="sources-section"
      className={`${styles.presentationDrawer} ${styles.nodisplay}`}
      hidden
      summaryClassName={styles.presentationDrawerSummary}
      bodyClassName={styles.presentationDrawerBody}
      toggleTestId="ai-sources-toggle"
      contentTestId="ai-assistant-message-sources"
      summary={(
      <>
        <span className={styles.presentationDrawerLabel}>
          <FileText size={14} />
          {title ?? `Sources (${consumerSources.length})`}
        </span>
        <ChevronDown size={14} className={styles.presentationDrawerChevron} />
      </>
      )}
    >
      {sourceItems}
    </PresentationDisclosure>
  );
};

const relatedLinkAccessibleLabel = (item: {
  label: string;
  meta?: string | null;
  preview?: string | null;
}): string =>
  [item.label, item.meta, item.preview].map((value) => String(value ?? '').trim()).filter(Boolean).join(' - ');

const RelatedLinksSection = ({ title, groups }: { title?: string | null; groups: AssistantPresentationModel['sections'][number] extends never ? never : any }) => (
  <PresentationDisclosure
    testId="ai-related-links-section"
    disclosureId="related-links-section"
    className={styles.presentationDrawer}
    defaultOpen
    summaryClassName={styles.presentationDrawerSummary}
    bodyClassName={styles.presentationDrawerBody}
    toggleTestId="ai-related-links-toggle"
    contentTestId="ai-citation-route-links"
    summary={(
      <>
        <span className={styles.presentationDrawerLabel}>
          <Link2 size={14} />
          {title ?? `Related links (${groups.reduce((count: number, group: { items: unknown[] }) => count + group.items.length, 0)})`}
        </span>
        <ChevronDown size={14} className={styles.presentationDrawerChevron} />
      </>
    )}
  >
      {groups.map((group: { id: string; label: string; items: Array<{ id: string; href: string; label: string; meta?: string | null; preview?: string | null; external?: boolean }> }) => (
        <section key={group.id} data-testid={`ai-related-links-group-${group.id}`}>
          <p className={styles.presentationSectionEyebrow}>{group.label}</p>
          <div className={styles.presentationDrawerList}>
            {group.items.map((item, index) => (
              <a
                key={item.id}
                href={item.href}
                className={styles.presentationListCard}
                data-testid={`ai-citation-route-link-${index + 1}`}
                aria-label={relatedLinkAccessibleLabel(item)}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noreferrer noopener' : undefined}
                title={item.preview ?? undefined}
              >
                <div className={styles.presentationListIcon}>
                  <FolderKanban size={14} />
                </div>
                <div className={styles.presentationListContent}>
                  <div className={styles.presentationListTitleRow}>
                    <p className={styles.presentationListTitle} data-link-label="true">{item.label}</p>
                    {item.external ? <ExternalLink size={12} className={styles.presentationActionIcon} /> : null}
                  </div>
                  {item.meta ? <p className={styles.presentationListMeta}>{item.meta}</p> : null}
                  {item.preview ? <p className={styles.presentationListPreview}>{item.preview}</p> : null}
                </div>
              </a>
            ))}
          </div>
        </section>
      ))}
  </PresentationDisclosure>
);

const DiagnosticsSection = ({
  title,
  summary,
  explanation,
  operatorGuidance,
  targetRoute,
  defaultOpen,
  displayMode,
}: {
  title?: string | null;
  summary: string;
  explanation?: string | null;
  operatorGuidance?: string | null;
  targetRoute?: string | null;
  defaultOpen?: boolean;
  displayMode?: 'icon' | 'drawer';
}) => {
  const normalizedTitle = !title || title === 'Diagnostics' ? 'Response explanation' : title;
  const tooltip = [normalizedTitle, summary, explanation, operatorGuidance]
    .filter((value): value is string => Boolean(value && value.trim().length > 0))
    .join('\n\n');

  if (displayMode === 'icon') {
    return (
      <div
        className={styles.presentationDiagnosticsIconRow}
        data-testid="ai-presentation-diagnostics"
        data-ai-diagnostics-display-mode="icon"
      >
        <span
          className={styles.presentationDiagnosticsIcon}
          aria-label={normalizedTitle}
          title={tooltip}
        >
          <TriangleAlert size={14} />
        </span>
      </div>
    );
  }

  return (
    <PresentationDisclosure
      testId="ai-presentation-diagnostics"
      disclosureId="presentation-diagnostics"
      className={styles.presentationDrawer}
      summaryClassName={styles.presentationDrawerSummary}
      bodyClassName={styles.presentationDrawerBody}
      defaultOpen={defaultOpen}
      displayMode="drawer"
      summary={(
        <>
          <span className={styles.presentationDrawerLabel}>
            <TriangleAlert size={14} />
            {normalizedTitle}
          </span>
          <ChevronDown size={14} className={styles.presentationDrawerChevron} />
        </>
      )}
    >
        <p className={styles.presentationCardPreview}>{summary}</p>
        {explanation ? <p className={styles.presentationListPreview}>{explanation}</p> : null}
        {operatorGuidance ? <p className={styles.presentationWarningText}>{operatorGuidance}</p> : null}
        {targetRoute ? (
          <a href={targetRoute} className={styles.presentationSecondaryAction}>
            Open related route
          </a>
        ) : null}
    </PresentationDisclosure>
  );
};

export function AssistantPresentationRenderer({
  presentation,
  formatTextContent = defaultFormatTextContent,
  textRevealState = null,
  sectionRenderers,
  onSourceClick,
  getSourceTooltip,
  onTaskProposalStart,
  onTaskProposalAskInChat,
  onTaskProposalDismiss,
  activeRouteContext,
  onAssistantPolicyEvent,
  onOpenArtifactInGoogleSheets,
}: AssistantPresentationRendererProps) {
  const context = useMemo<AssistantPresentationRendererContext>(() => ({
    formatTextContent,
  }), [formatTextContent]);
  const exportMenuArtifacts = useMemo(
    () => collectAssistantPresentationExportArtifacts(presentation.sections, onOpenArtifactInGoogleSheets),
    [onOpenArtifactInGoogleSheets, presentation.sections],
  );
  const exportMenuTargetIndex = useMemo(
    () => resolveExportMenuTargetIndex(presentation.sections, exportMenuArtifacts),
    [exportMenuArtifacts, presentation.sections],
  );
  const renderExportMenu = (index: number) => (
    index === exportMenuTargetIndex
      ? (
          <AssistantPresentationExportMenu
            items={exportMenuArtifacts}
            activeRouteContext={activeRouteContext}
            onAssistantPolicyEvent={onAssistantPolicyEvent}
            onOpenArtifactInGoogleSheets={onOpenArtifactInGoogleSheets}
          />
        )
      : null
  );
  const multiArtifactTabs = useMemo(
    () => buildMultiArtifactBrowserTabs(presentation),
    [presentation],
  );
  const multiArtifactTabIndexes = useMemo(
    () => new Set(multiArtifactTabs.map((tab) => tab.originalIndex)),
    [multiArtifactTabs],
  );
  const firstMultiArtifactTabIndex = multiArtifactTabs[0]?.originalIndex ?? -1;
  const textRevealTargetIndex = useMemo(() => {
    const summaryIndex = presentation.sections.findIndex((section) => section.type === 'summary');
    if (summaryIndex >= 0) {
      return summaryIndex;
    }
    return presentation.sections.findIndex((section) => section.type === 'formatted_body');
  }, [presentation.sections]);
  const renderTextRevealBlock = () => (
    <div
      className={`${styles.presentationTextBlock} ${styles.messageRevealContent}`}
      data-testid="ai-message-streaming-text"
      data-stream-state={textRevealState?.phase === 'handoff' ? 'handoff' : 'revealing'}
    >
      {textRevealState?.content}
      {textRevealState?.showCursor ? (
        <span
          className={styles.messageRevealCursor}
          data-testid="ai-message-streaming-cursor"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
  const handleArtifactAction = useCallback((
    item: AssistantPresentationArtifactItem,
    action: IntellioAssistantArtifactActionDescriptor,
  ) => {
    if (action.kind === 'download' && isDownloadablePresentationArtifact(item)) {
      downloadPresentationArtifact(item);
      return;
    }
    const href = readOptionalNonEmptyString(action.href);
    if ((action.kind === 'open_external' || action.kind === 'open_workspace') && href) {
      openExternalArtifactUrl(href);
      return;
    }
    void onAssistantPolicyEvent?.({
      eventId: `artifact-action-${action.id}-${Date.now()}`,
      type: 'artifact_action',
      policyEventType: action.policyEventType ?? action.kind,
      routeContextRef: activeRouteContext?.contextRef ?? null,
      routeRevision: activeRouteContext?.revision ?? null,
      routeFamily: activeRouteContext?.routeFamily ?? null,
      pathname: activeRouteContext?.pathname ?? null,
      actionId: action.id,
      artifactActionId: action.id,
      sourceRef: action.sourceRef ?? item.sourceRef ?? null,
      payload: buildArtifactPolicyEventPayload(item, action),
    });
  }, [activeRouteContext, onAssistantPolicyEvent]);
  const renderPresentationSection = (
    section: AssistantPresentationSection,
    index: number,
    options: { forceInlineArtifacts?: boolean; forceVisibleSources?: boolean } = {},
  ): React.ReactNode => {
    const exportMenu = renderExportMenu(index);
    const shouldRevealTextInSection = Boolean(textRevealState?.active)
      && index === textRevealTargetIndex
      && (section.type === 'summary' || section.type === 'formatted_body');
    const customRenderer = sectionRenderers?.[section.type];
    const shouldUseCustomRenderer = Boolean(customRenderer)
      && !shouldRevealTextInSection
      && !(
        section.type === 'artifacts'
        && options.forceInlineArtifacts
        && presentation.responseKind === 'multi_artifact_insight'
      );
    if (
      section.type === 'artifacts'
      && !options.forceInlineArtifacts
      && !shouldUseCustomRenderer
      && presentation.responseKind !== 'multi_artifact_insight'
      && !shouldRenderArtifactsSectionInline(section, onOpenArtifactInGoogleSheets)
    ) {
      return exportMenu ? (
        <section key={section.id ?? `${section.type}-${index + 1}`} className={styles.presentationSection}>
          <PresentationSectionHeadingRow exportMenu={exportMenu} />
        </section>
      ) : null;
    }
    if (shouldUseCustomRenderer && customRenderer) {
      return (
        <React.Fragment key={section.id ?? `${section.type}-${index + 1}`}>
          {customRenderer(section, context)}
        </React.Fragment>
      );
    }

    switch (section.type) {
      case 'summary':
        return (
          <section key={section.id ?? `${section.type}-${index + 1}`} className={styles.presentationSection}>
            <PresentationSectionHeadingRow title={section.title} exportMenu={exportMenu} />
            {shouldRevealTextInSection ? renderTextRevealBlock() : (
              <div data-testid="ai-message-content-assistant" className={styles.presentationTextBlock}>
                {formatTextContent(section.text)}
              </div>
            )}
          </section>
        );
      case 'table':
        return (
          <TableSection
            key={section.id ?? `${section.type}-${index + 1}`}
            sectionId={section.id}
            title={section.title}
            columns={section.columns}
            rows={section.rows}
            footerText={section.footerText}
            footerAction={section.footerAction}
            pagination={section.pagination}
            exportMenu={exportMenu}
          />
        );
      case 'chart':
        return (
          <ChartSection
            key={section.id ?? `${section.type}-${index + 1}`}
            sectionId={section.id}
            title={section.title}
            chartTitle={section.chartTitle}
            items={section.items}
            maxValue={section.maxValue}
            exportMenu={exportMenu}
          />
        );
      case 'metric_grid':
        return <MetricGridSection key={section.id ?? `${section.type}-${index + 1}`} title={section.title} items={section.items} exportMenu={exportMenu} />;
      case 'checklist':
        return <ChecklistSection key={section.id ?? `${section.type}-${index + 1}`} title={section.title} items={section.items} exportMenu={exportMenu} pagination={section.pagination} />;
      case 'formatted_body':
        return (
          <section key={section.id ?? `${section.type}-${index + 1}`} className={styles.presentationSection}>
            <PresentationSectionHeadingRow title={section.title} exportMenu={exportMenu} />
            {shouldRevealTextInSection ? renderTextRevealBlock() : (
              <div className={styles.presentationTextBlock}>
                {formatTextContent(section.text)}
              </div>
            )}
          </section>
        );
      case 'result_cards':
        return (
          <ResultCardsSection
            key={section.id ?? `${section.type}-${index + 1}`}
            title={section.title}
            cards={section.cards}
            pagination={section.pagination}
            exportMenu={exportMenu}
            displayStrategy={resolveResultCardsDisplayStrategy({
              sectionStrategy: section.displayStrategy,
              responseKind: presentation.responseKind,
              title: section.title,
              cards: section.cards,
            })}
          />
        );
      case 'confirmation':
        return (
          <ConfirmationSection
            key={section.id ?? `${section.type}-${index + 1}`}
            title={section.title}
            tone={section.tone}
            titleText={section.titleText}
            summary={section.summary}
            meta={section.meta}
            exportMenu={exportMenu}
          />
        );
      case 'artifacts':
        return (
          <ArtifactsSection
            key={section.id ?? `${section.type}-${index + 1}`}
            title={section.title}
            items={section.items}
            exportMenu={exportMenu}
            onArtifactAction={handleArtifactAction}
          />
        );
      case 'task_proposal':
        return (
          <TaskProposalSection
            key={section.id ?? `${section.type}-${index + 1}`}
            title={section.title}
            proposal={section.proposal}
            onStart={onTaskProposalStart}
            onAskInChat={onTaskProposalAskInChat}
            onDismiss={onTaskProposalDismiss}
          />
        );
      case 'task_thread':
        return <TaskThreadSection key={section.id ?? `${section.type}-${index + 1}`} title={section.title} thread={section.thread} />;
      case 'sources':
        return (
          <SourcesSection
            key={section.id ?? `${section.type}-${index + 1}`}
            title={section.title}
            items={section.items}
            onSourceClick={onSourceClick}
            getSourceTooltip={getSourceTooltip}
            inline={options.forceVisibleSources}
          />
        );
      case 'related_links':
        return <RelatedLinksSection key={section.id ?? `${section.type}-${index + 1}`} title={section.title} groups={section.groups} />;
      case 'diagnostics':
        return (
          <DiagnosticsSection
            key={section.id ?? `${section.type}-${index + 1}`}
            title={section.title ?? section.diagnostics.title}
            summary={section.diagnostics.summary}
            explanation={section.diagnostics.explanation}
            operatorGuidance={section.diagnostics.operatorGuidance}
            targetRoute={section.diagnostics.targetRoute}
            defaultOpen={section.diagnostics.defaultOpen}
            displayMode={section.diagnostics.displayMode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={styles.presentationStack}
      data-testid="ai-response-presentation"
      data-response-kind={presentation.responseKind ?? undefined}
      data-active-route-context-ref={activeRouteContext?.contextRef ?? undefined}
      data-route-context-ref={activeRouteContext?.contextRef ?? undefined}
      data-active-route-family={activeRouteContext?.routeFamily ?? undefined}
      data-active-route-pathname={activeRouteContext?.pathname ?? undefined}
      data-active-route-revision={activeRouteContext?.revision ?? undefined}
    >
      {presentation.sections.map((section, index) => {
        if (multiArtifactTabIndexes.has(index)) {
          if (index !== firstMultiArtifactTabIndex) {
            return null;
          }
          return (
            <MultiArtifactBrowser
              key="multi-artifact-insight-browser"
              preferredArtifactType={presentation.preferredArtifactType}
              tabs={multiArtifactTabs}
              renderTabSection={(tab) => renderPresentationSection(
                tab.section,
                tab.originalIndex,
                { forceInlineArtifacts: true, forceVisibleSources: true },
              )}
            />
          );
        }
        return renderPresentationSection(section, index);
      })}
    </div>
  );
}
